import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col, Accordion, Image, Modal, Carousel } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";

import { submitLead } from "../leadService";
import LayerFour from "../assets/images/layers/Layer_4.png";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Offcanvas from "react-bootstrap/Offcanvas";
import Navbar from "react-bootstrap/Navbar";
import { CiMenuFries } from "react-icons/ci";
import Logo from "../assets/images/black-logo.png";
import "../assets/css/AIFirstDeliveryCenter.css";
import Footer from "./common/footer";

import { Zap, Shield, TrendingUp, Users, Building2, Cloud, Settings, Eye, DollarSign, Repeat, ShieldCheck, Brain, CheckCircle2, Car, Home, ShoppingBag, Briefcase, ArrowLeftRight, Quote } from "lucide-react";
import flutterHero from "../assets/images/banner/salesforce-partner.png";
import whoImage from "../assets/images/banner/who.png";

const useCounter = (target, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          let start = 0;
          const increment = target / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );

    const node = ref.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [target, duration]);

  return [count, ref];
};

const DataReadinessModalContent = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    mobile: "",
    company: "",
    jobTitle: "",
    message: "",
    role: "",
    subscribe: "yes",
    heardAbout: "",
    otherChallenge: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({});

  const businessEmailRegex =
    /@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|aol\.com|icloud\.com|protonmail\.com|zoho\.com|mail\.com|gmx\.com)$/i;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "First name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (businessEmailRegex.test(formData.email)) {
      newErrors.email = "Please use a business email address";
    }

    if (!formData.mobile) {
      newErrors.mobile = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit phone number";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = "Job title is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage("");
    setErrors({});

    const { email } = formData;

    if (!validateForm()) {
      setLoading(false);
      setResponseMessage("Please correct the errors in the form.");
      return;
    }

    if (businessEmailRegex.test(email)) {
      setResponseMessage("Please enter a valid business email address.");
      setLoading(false);
      return;
    }


    try {
      const response = await submitLead("salesforce-assessment", formData);
      if (response.success) {
        setResponseMessage("Form submitted successfully!");
        setIsSubmitted(true);
        setTimeout(() => {
          setResponseMessage("");
        }, 3000);
        setFormData({
          name: "",
          email: "",
          mobile: "",
          company: "",
          message: "",
          jobTitle: "",
          role: "",
          subscribe: "yes",
          heardAbout: "",
          lastName: "",
          otherChallenge: "",
        });
      } else {
        setResponseMessage(response.message);
      }
    } catch (error) {
      setResponseMessage("An error occurred. Please try again.");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="bg-E2F1FE position-relative box-shadow-one rounded-4 pt-5 p-23 p-sm-23">
        <Image
          src={LayerFour}
          className="position-absolute bottom-0 start-0 w-40"
        />

        <Row className="position-relative form-box z-0">
          {isSubmitted ? null : (
            <Col lg={5}>
              <div className="text-left d-flex justify-content-start">
                <h2 className="section-heading fw-700 fs-2">
                  Book a Free
                  <span className="" style={{ color: "#028bc6" }}>
                    {" "}
                    Salesforce
                  </span>{" "}
                  <span className="bordered-text bordered-text-start">
                    Assessment
                  </span>
                </h2>
              </div>
            </Col>
          )}
          <Col lg={isSubmitted ? 12 : 7}>
            {isSubmitted ? (
              <div className="text-center py-5">
                <div className="d-flex flex-column align-items-center">
                  <h2 className="section-heading fw-700 fs-2 mb-4">
                    <span className="bordered-text">Thank You!</span>
                  </h2>
                  <p className="fs-4 fw-700 mb-2">
                    Book a Free Salesforce Assessment
                  </p>
                  <p className="fs-5 fw-700">
                    Our team will get back to you shortly.
                  </p>
                  <Button
                    className="mt-4 d-inline text-white btn btn-danger btn-xl me-3 d-none"
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({
                        name: "",
                        email: "",
                        company: "",
                        message: "",
                        role: "",
                        subscribe: "yes",
                        heardAbout: "",
                        jobTitle: "",
                        lastName: "",
                        mobile: "",
                        otherChallenge: "",
                      });
                    }}
                  >
                    Submit Another Request
                  </Button>
                </div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Row className="pb-3 mt-3 mt-lg-0">
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Name <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.name && (
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  {/* <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        isInvalid={!!errors.lastName}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.lastName && (
                        <Form.Control.Feedback type="invalid">
                          {errors.lastName}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col> */}
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Company <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        isInvalid={!!errors.company}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.company && (
                        <Form.Control.Feedback type="invalid">
                          {errors.company}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Email <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder=""
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.email && (
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>Job Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        isInvalid={!!errors.jobTitle}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.jobTitle && (
                        <Form.Control.Feedback type="invalid">
                          {errors.jobTitle}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="mobile"
                        placeholder="Enter 10-digit phone number"
                        value={formData.mobile}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d]/g, "")
                            .slice(0, 10);
                          setFormData({ ...formData, mobile: value });
                        }}
                        isInvalid={!!errors.mobile}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.mobile && (
                        <Form.Control.Feedback type="invalid">
                          {errors.mobile}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  {/* <Col lg={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>How did you hear about us?</Form.Label>
                      <Form.Select
                        name="heardAbout"
                        value={formData.heardAbout}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                        isInvalid={!!errors.heardAbout}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="Social Media">Social Media</option>
                        <option value="Friend">Friend</option>
                        <option value="Search Engine">Search Engine</option>
                        <option value="Advertisement">Advertisement</option>
                        <option value="Event">Event</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {errors.heardAbout && (
                        <Form.Control.Feedback type="invalid">
                          {errors.heardAbout}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>What is your role?</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role || ""}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                        isInvalid={!!errors.role}
                      >
                        <option value="">Select</option>
                        <option value="Developer">Developer</option>
                        <option value="Manager">Manager</option>
                        <option value="Designer">Designer</option>
                        <option value="Marketer">Marketer</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {errors.role && (
                        <Form.Control.Feedback type="invalid">
                          {errors.role}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        How can we help your organization?
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder=""
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        isInvalid={!!errors.message}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.message && (
                        <Form.Control.Feedback type="invalid">
                          {errors.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col> */}
                  {/* <Col lg={12}>
                    <Form.Group className="mb-3 mt-1">
                      <Form.Label className="mb-3">
                        I would like to receive emails from{" "}
                        <span className="fw-700">Comsense</span> with exclusive
                        updates on products, services, events, and alerts. I may
                        unsubscribe at any time.
                      </Form.Label>
                      <div>
                        <Form.Check
                          type="radio"
                          label="Yes"
                          name="subscribe"
                          value="yes"
                          className="d-block"
                          checked={formData.subscribe === "yes"}
                          onChange={handleChange}
                          inline
                        />
                        <Form.Check
                          type="radio"
                          label="No"
                          name="subscribe"
                          value="no"
                          className="d-block"
                          checked={formData.subscribe === "no"}
                          onChange={handleChange}
                          inline
                        />
                      </div>
                    </Form.Group>
                  </Col> */}

                  <Col lg={12} className="mt-4 text-start">
                    <>
                      <Button
                        variant=""
                        type="submit"
                        className="fw-600 d-block btn btn-danger btn-xl"
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                    </>
                  </Col>
                  {responseMessage && (
                    <p
                      className="mt-3 mb-0 fs-12 text-center"
                      style={{ color: loading ? "blue" : "red" }}
                    >
                      {responseMessage}
                    </p>
                  )}
                </Row>
              </Form>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

const SalesforceLanding = () => {
  const [showDataReadinessModal, setShowDataReadinessModal] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  
  const [count1, ref1] = useCounter(100);
  const [count2, ref2] = useCounter(2000);
  const [count3, ref3] = useCounter(1800);

  const openDataReadinessModal = () => setShowDataReadinessModal(true);
  const closeDataReadinessModal = () => setShowDataReadinessModal(false);

  const [show, setShow] = useState(false);
  const location = useLocation();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.scrollTo(0, 0);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [location]);

  const salesforceServices = [
    {
      id: "managed",
      title: "Salesforce Managed Services",
      icon: ShieldCheck,
      angle: -90,
      details: [
        "Administration & Governance",
        "User Support & Release Management",
        "Security Reviews",
        "Performance Monitoring",
        "Continuous Enhancements",
      ],
    },
    {
      id: "ai",
      title: "Salesforce AI & Automation",
      icon: Zap,
      angle: -38.5,
      details: [
        "Predictive Sales Insights",
        "Lead Scoring",
        "Customer Service Automation",
        "Intelligent Workflow Automation",
        "AI-Powered Recommendations",
      ],
    },
    {
      id: "consulting",
      title: "Salesforce Consulting",
      icon: Brain,
      angle: 13,
      details: [
        "Salesforce Health Assessment",
        "Business Process Optimization",
        "Salesforce Architecture Design",
        "Digital Transformation Consulting",
        "User Adoption & Change Management",
      ],
    },
    {
      id: "implementation",
      title: "Salesforce Implementation",
      icon: Cloud,
      angle: 64.5,
      details: [
        "Discovery & Requirement Analysis",
        "Salesforce Configuration",
        "Workflow Automation",
        "Custom Development",
        "User Training & Deployment",
      ],
    },
    {
      id: "customization",
      title: "Salesforce Customization",
      icon: Settings,
      angle: 116,
      details: [
        "Apex Development",
        "Lightning Components",
        "Custom Objects & Workflows",
        "Dashboards & Reporting",
        "Experience Cloud Portals",
      ],
    },
    {
      id: "integration",
      title: "Salesforce Integration",
      icon: ArrowLeftRight,
      angle: 167.5,
      details: [
        "Microsoft Dynamics & SAP",
        "Oracle & Power BI",
        "ERP & Marketing Platforms",
        "Custom APIs",
        "Real-time Data Sync",
      ],
    },
    {
      id: "migration",
      title: "Salesforce Migration",
      icon: Repeat,
      angle: 219,
      details: [
        "Data Assessment & Cleansing",
        "Migration Planning",
        "Data Mapping & Validation",
        "Testing & Quality Assurance",
        "Post-Migration Support",
      ],
    },
  ];

  const [hoveredService, setHoveredService] = useState(salesforceServices[0]);

  return (
    <>
      <Navbar
        collapseOnSelect
        expand="lg"
        sticky="top"
        className={`navbar_menu ${scrolled ? "navbar-scrolled" : ""}`}
      >
        <Container>
          <Navbar.Brand href="/">
            <Image width={150} src={Logo} />
          </Navbar.Brand>
          {/* Mobile Offcanvas Toggle Button */}
          <div className="d-lg-none">
            <Button variant="light" onClick={handleShow}>
              <CiMenuFries />
            </Button>
          </div>
          {/* Desktop Menu */}
          <Navbar.Collapse id="responsive-navbar-nav">
            <Nav className="m-auto">
              <Nav.Link
                href="#salesforceServices"
                active={activeSection === "salesforceServices"}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveSection("salesforceServices");
                  const section = document.getElementById("salesforceServices");
                  if (section) {
                    section.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                Salesforce Services
              </Nav.Link>
              <Nav.Link
                href="#FAQs"
                active={activeSection === "FAQs"}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveSection("FAQs");
                  const section = document.getElementById("FAQs");
                  if (section) {
                    section.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                FAQs
              </Nav.Link>
            </Nav>
            <Nav>
              <div className="text-center">
                <Nav.Link
                  className="btn blue-btn"
                  onClick={openDataReadinessModal}
                  type="button"

                  style={{
                    background:
                      "linear-gradient(180deg, #067BB1 0%, #04467A 100%)",
                  }}
                >
                  Book a Free Salesforce Assessment
                </Nav.Link>
              </div>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Mobile Offcanvas */}
      <Offcanvas show={show} onHide={handleClose} className="d-lg-none">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav className="flex-column">
            <Nav.Link
              href="#salesforceServices"
              active={activeSection === "salesforceServices"}
              onClick={(e) => {
                e.preventDefault();
                setActiveSection("salesforceServices");
                const section = document.getElementById("salesforceServices");
                if (section) {
                  section.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              Salesforce Services
            </Nav.Link>
            <Nav.Link
              href="#FAQs"
              active={activeSection === "FAQs"}
              onClick={(e) => {
                e.preventDefault();
                setActiveSection("FAQs");
                const section = document.getElementById("FAQs");
                if (section) {
                  section.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              FAQs
            </Nav.Link>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>
      <Helmet>
        <title>
          Salesforce Consulting, Implementation & Managed Services | Comsense
        </title>
        <meta
          name="description"
          content="Unlock the full value of Salesforce with expert consulting, implementation and managed services from Comsense. Drive efficiency, customer engagement and ROI with end-to-end Salesforce solutions."
        />
        <meta
          name="keywords"
          content="Salesforce consulting, Salesforce implementation, Salesforce managed services, Salesforce integration, Salesforce migration, AI automation, CRM services"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Comsense Technologies" />
        <meta
          property="og:title"
          content="Salesforce Consulting, Implementation & Managed Services | Comsense"
        />
        <meta
          property="og:description"
          content="Unlock the full value of Salesforce with expert consulting, implementation and managed services from Comsense."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.comsensetechnologies.com/salesforce-services"
        />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');
        .gcc-outfit { font-family: 'Outfit', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; }
        .gcc-outfit * { font-family: 'Outfit', sans-serif; }

        html {
          scroll-behavior: smooth;
        }

        #salesforceServices, #FAQs {
          scroll-margin-top: 100px;
        }

        @keyframes floatY { 0% { transform: translateY(0); } 50% { transform: translateY(-8px); } 100% { transform: translateY(0); } }
        @keyframes floatX { 0% { transform: translateX(0); } 50% { transform: translateX(8px); } 100% { transform: translateX(0); } }

        .hero-tag { display:inline-flex; align-items:center; gap:8px; padding:6px 12px; border-radius:999px; background:#E2F1FE; color:#028bc6; border:1px solid #b8daff; font-weight:700; font-size:12px; }
        .hero-title { font-weight:800; font-size:42px; line-height:1.1; letter-spacing:-0.02rem; margin-bottom:24px; }
        .hero-subtitle { font-size:1.1rem; line-height:1.8; color:#334155; max-width:780px; margin-bottom:24px; }
        .highlight-red { color:#028bc6; }
        .hero-chip-row { display:flex; gap:12px; flex-wrap:wrap; margin:18px 0 28px; }
              .hero-media { border-radius:0px; overflow:hidden; }

        .bannerShapeBottom { opacity:0.35; }
        .ls-minus-045 { letter-spacing:-0.045em; }
        .text-black { color:#0f172a !important; }
        .btn-red { text-decoration:none; background:#e00; color:#fff; border:none; border-radius:12px; padding:10px 18px; font-weight:700; box-shadow:0 12px 24px rgba(238,0,0,0.18); }
        .btn-red:hover { transform:translateY(-1px); }

        .section-card { background:#fff; border:1px solid #E2E8F0; border-radius:24px; padding:28px; height:100%; transition:transform .25s ease, border-color .25s ease, box-shadow .25s ease; }
        .section-card:hover { transform:translateY(-4px); border-color:#0F172A12; box-shadow:0 16px 36px rgba(15,23,42,0.08); }
        .section-card h3 { font-size:1.25rem; font-weight:800; margin-bottom:14px; }
        .section-card p, .section-card li { color:#475569; line-height:1.75; }
        .service-card { border-radius:24px; background:#fff; border:1px solid #E2E8F0; padding:28px; margin-bottom:20px; height:100%; }
        .service-card h4 { font-size:1.15rem; font-weight:800; margin-bottom:14px; }
        .service-card ul { padding-left:18px; margin:0; }
        .service-card li { margin-bottom:10px; }
        .cloud-item, .industry-item, .framework-item, .outcome-item { background:#fff; border:1px solid #E2E8F0; border-radius:20px; padding:22px; height:100%; transition:transform .25s ease; }
        .cloud-item:hover, .industry-item:hover, .framework-item:hover, .outcome-item:hover { transform:translateY(-3px); }
        .cloud-title, .industry-title, .framework-title, .outcome-title { font-weight:800; margin-bottom:10px; color:#0F172A; }
        .cloud-text, .industry-text, .framework-text, .outcome-text { color:#475569; line-height:1.75; }
        .business-outcome-card { background: #ffffff; border: 2px solid rgba(6,123,177,0.12); border-radius: 24px; padding: 34px 28px; box-shadow: 0 22px 55px rgba(6,123,177,0.08); min-height: 220px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 18px; transition: transform .35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow .35s ease, border-color .35s ease; position: relative; overflow: hidden; }
        .business-outcome-card:hover { transform: translateY(-3px); border-color: rgba(6,123,177,0.3); box-shadow: 0 20px 40px -12px rgba(6,123,177,0.15); }
        .business-outcome-card::before { content: ''; position: absolute; top: 0; left: 24px; right: 24px; height: 3px; background: linear-gradient(90deg, transparent, rgba(6,123,177,0.65), transparent); opacity: 0; transition: opacity .35s ease; }
        .business-outcome-card::after { content: ''; position: absolute; bottom: 14px; left: 24px; right: 24px; height: 1px;  box-shadow: 0 -10px 30px rgba(6,123,177,0.12); opacity: 0; transition: opacity .35s ease; }
        .business-outcome-card:hover::before,
        .business-outcome-card:hover::after { opacity: 1; }
        .outcome-corner-tr { position: absolute; top: 16px; right: 16px; width: 24px; height: 24px; border-top: 2px solid rgba(6,123,177,0.2); border-right: 2px solid rgba(6,123,177,0.2); border-top-right-radius: 12px; z-index: 2; }
        .outcome-corner-bl { position: absolute; bottom: 16px; left: 16px; width: 24px; height: 24px; border-bottom: 2px solid rgba(6,123,177,0.2); border-left: 2px solid rgba(6,123,177,0.2); border-bottom-left-radius: 12px; z-index: 2; }
        .business-outcome-card:hover .outcome-corner-tr { border-color: rgba(6,123,177,0.6); }
        .business-outcome-card:hover .outcome-corner-bl { border-color: rgba(6,123,177,0.6); }
        .business-outcome-card:hover .business-outcome-icon { transform: scale(1.08); box-shadow: 0 12px 30px rgba(6,123,177,0.25); }
        .business-outcome-icon { width: 64px; height: 64px; border-radius: 22px; display: inline-flex; align-items: center; justify-content: center; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); color: #fff; box-shadow: 0 16px 40px rgba(6,123,177,0.08); z-index: 1; transition: transform .35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow .35s ease; }
        .business-outcome-text { font-size: 1rem; font-weight: 700; color: #0f172a; line-height: 1.4; margin: 0; text-align: center; z-index: 1; max-width: 280px; transition: color .35s ease; }
        .business-outcome-card:hover .business-outcome-text { color: #0b172a; }
        .business-outcome-card p { margin: 0; padding: 0; }

        .faq-section { background:#fff; padding:70px 0 70px 0; }
        .faq-heading { font-size:2rem; font-weight:800; margin-bottom:24px; }
        .faq-card { border:none !important; border-radius:20px; overflow:hidden; margin-bottom:16px; }
        .accordion-button { border-radius:18px; background:#fff; color:#0F172A; font-size:1rem; font-weight:700; padding:18px 24px; }
        .accordion-button:not(.collapsed) { color:#0F172A; background:#E2F1FE; box-shadow:none; }
        .accordion-body { background:#fff; border-radius:0 0 18px 18px; color:#475569; padding:20px 24px; }
        .last-cta { background:#E2F1FE; border-radius:32px; padding:60px 30px; margin-top:40px; }
        @media (max-width: 991px) { .hero-title { font-size:2.4rem; } .hero-chip-row { justify-content:center; } }

        /* Stats Section */
        .stats-section { padding: 80px 0; background: #f8fafc; }
        .stat-card-box { 
          background: #ffffff; 
          border-radius: 24px; 
          padding: 32px 32px; 
          box-shadow: 0 10px 40px rgba(0,0,0,0.06);
          text-align: center; 
          position: relative; 
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          width: 100%;
          max-width: 340px;
          border: 3px solid rgba(143, 197, 222, 0.15);
        }
        .stat-card-box:hover { 
          transform: translateY(-8px); 
          box-shadow: 0 20px 50px rgba(0,0,0,0.1);
        }
        .stat-number-wrapper { margin-bottom: 12px; }
        .stat-number { 
          font-size: 38px; 
          font-weight: 800; 
          color: #0f172a; 
          line-height: 1;
          display: block;
        }
        .stat-label {
          font-size: 16px;
          color: #64748b;
          font-weight: 600;
          margin: 0;
        }
        .hero-bottom-shape {
          position: relative;
          bottom: 0;
          left: 0;
          width: 100%;
          overflow: hidden;
          margin-top: 0px;
        }
        .stat-decoration-top-left {
          position: absolute;
          top: -30px;
          left: -30px;
          opacity: 0.5;
          animation: floatY 7s ease-in-out infinite;
          z-index: 1;
        }
        .stat-decoration-bottom-right {
          position: absolute;
          bottom: -30px;
          right: -30px;
          opacity: 0.5;
          animation: floatY 6s ease-in-out infinite reverse;
          z-index: 1;
        }
        @media (max-width: 991px) {
          .stat-card-box { padding: 40px 24px; max-width: 100%; }
          .stat-number { font-size: 48px; }
        }
        
        /* Who We Are Section */
        .who-we-are-section {
          padding: 80px 0;
        }
        .who-we-are-title {
          font-family: 'Outfit', sans-serif;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 24px;
          line-height: 1.1;
          font-size: 42px;
  font-weight: 800;
  color: #000;
  letter-spacing: -0.02em;
  margin-bottom: 24px;
        }
        .text-blue {
          color: #067bb1;
        }
        .who-we-are-text {
          font-family: 'Outfit', sans-serif;
          font-size: 16px;
          color: #475569;
          line-height: 1.7;
        }
        .who-we-are-image-wrapper {
          text-align: center;
        }
        .who-we-are-image {
          max-width: 100%;
          height: auto;
        }
        .certifications-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          justify-items: center;
        }
        .certification-badge {
          transition: transform 0.3s ease;
        }
        .certification-badge:hover {
          transform: scale(1.05);
        }
        
        @media (max-width: 991px) {
          .who-we-are-title {
            font-size: 36px;
          }
          .certifications-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
        }
        
        @media (max-width: 576px) {
          .who-we-are-title {
            font-size: 28px;
          }
          .certifications-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
        }
      `}</style>
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');              .gcc-outfit { font-family: 'Outfit', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, 'Apple Color Emoji', 'Segoe UI Emoji'; }
  
.gcc-outfit  *{ font-family: "Outfit", sans-serif;}

              @keyframes floatY { 0% { transform: translateY(0); } 50% { transform: translateY(-8px); } 100% { transform: translateY(0); } }
              @keyframes floatX { 0% { transform: translateX(0); } 50% { transform: translateX(8px); } 100% { transform: translateX(0); } }
              @keyframes pulseGlow { 0% { box-shadow: 0 8px 24px rgba(0,0,0,0.08); }
                                     50% { box-shadow: 0 12px 28px rgba(0,0,0,0.12); }
                                     100% { box-shadow: 0 8px 24px rgba(0,0,0,0.08); } }
              .shape-float-y { animation: floatY 12s ease-in-out infinite; }
              .shape-float-x { animation: floatX 14s ease-in-out infinite; }
              .card-modern { border-radius: 12px; background: #fff; box-shadow: 0 8px 24px rgba(0,0,0,0.08); transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease; border: 1px solid transparent; }
              .card-modern:hover { transform: translateY(-4px); animation: pulseGlow 1.6s ease-in-out; border-color: #067bb1; box-shadow: 0 12px 32px rgba(6,123,177,0.15); }
              .card-icon { width:40px; height:40px; border-radius:50%; display:flex; align-items:center; justify-content:center; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); border:1px solid #067bb1; color: #fff; }
              .card-accent-red { border-top:4px solid #067bb1; }
              .card-accent-blue { border-top:4px solid #2563eb; }
              .card-accent-violet { border-top:4px solid #7c3aed; }
              .badge-pill { display:inline-block; padding:6px 12px; border-radius:999px; font-size:12px; font-weight:600; }
              .badge-prime { background:#E2F1FE; color:#067bb1; border:1px solid #067bb1; }
              .badge-partner { background:#eef6ff; color:#2563eb; border:1px solid #2563eb; }
              .stat-chip { display:inline-flex; align-items:center; gap:6px; padding:8px 12px; border-radius:999px; background:#f7fbff; border:1px solid #e6f0ff; font-weight:600; font-size:12px; }
              .card-title-lg { font-size: 1.4rem; letter-spacing: -0.02rem; }
              .card-subtitle-sm { font-size: 12px; color: #64748b; font-weight: 600; }
              /* Hero Enhancements */
              .hero-tag { display:inline-flex; align-items:center; gap:8px; padding:6px 12px; border-radius:999px; background:#E2F1FE; color:#028bc6; border:1px solid #b8daff; font-weight:700; font-size:12px; }
              .hero-title { font-weight:800; font-size:48px; line-height:1.1; letter-spacing:-0.02rem; }
              .highlight-red { color:#028bc6;  }
              .gcc-highlight-pill { display:inline-block; padding: 10px 18px; border-radius: 999px; background: rgba(6,123,177,0.10); border: 1px solid rgba(6,123,177,0.28); color: #04467a; box-shadow: 0 12px 28px rgba(6,123,177,0.14); }
              .gcc-highlight-marker { color: #04467a; padding: 0 6px; background: linear-gradient(transparent 62%, rgba(6,123,177,0.18) 62%); border-radius: 8px; }
              .hero-chip-row { display:flex; gap:12px; flex-wrap:wrap; margin:18px 0; }
              .hero-media { border-radius:16px; overflow:hidden; 
            //   box-shadow:0 20px 60px rgba(16,24,40,0.12);
               }
              .btn-red { text-decoration:none;background: #e00; color:#fff; border:none; border-radius:12px; padding:10px 18px; font-weight:700; box-shadow:0 12px 24px rgba(238,0,0,0.18);padding:10px 18px; }
              .btn-outline-dark { text-decoration:none;background:#fff; color:#0f172a; border:1px solid #e5e7eb; border-radius:12px; padding:10px 18px; font-weight:700; padding:10px 18px; }
              /* Hero Stat Cards (icon chips) */
              .stat-card { display:flex; align-items:center; gap:12px; }
              .stat-icon { width:36px; height:36px; border-radius:12px; display:flex; align-items:center; justify-content:center; border:1px solid transparent; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); color: #fff; }
              .stat-icon-green { background: #f0fff5;
  border-color: #d7fbe7;
  color: #10b981; }
              .stat-icon-orange {   background: #fff7ed;
  border-color: #fde2c5;
  color: #f59e0b; }
              .stat-icon-pink { background: #fdf2f6;
  border-color: #f8d7e5;
  color: #ef4444; }
              .stat-label { font-size:14px; color:#64748b; }
              .stat-value { font-size:16px; font-weight:800; color:#0f172a; }
              /* Pods card styling inspired by reference card */
              .pod-card { position: relative; overflow: hidden; border: 1px solid #e6f0ff; }
              .pod-shadow-red { box-shadow: 0 6px 0 rgba(238,0,0,0.15); }
              .pod-shadow-blue { box-shadow: 0 6px 0 rgba(37,99,235,0.15); }
              .pod-shadow-violet { box-shadow: 0 6px 0 rgba(124,58,237,0.15); }
              .pod-title { font-size:1.25rem; letter-spacing:-0.02rem; color:#0f172a; }
              .pod-list { font-size:13px; color:#334155; }
              .pod-accent-shape { position:absolute; bottom:10px; right:10px; width:68px; height:68px; border-radius:999px; background: radial-gradient(circle at 40% 40%, rgba(37,99,235,0.14) 0%, rgba(37,99,235,0.08) 45%, transparent 70%); }
              
              /* The Problem Section */
              .problem-section { padding: 70px 0 70px 0; background: #fff; position: relative; overflow: hidden; }
              .problem-pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 999px; background: #E2F1FE; color: #067bb1; border: 1px solid #b8daff; font-size: 12px; font-weight: 700; margin-bottom: 24px; }
              .problem-title { font-size: 42px; font-weight: 800; color: #000; letter-spacing: -0.02em; margin-bottom: 24px; }
              .problem-desc { font-size: 18px; color: #475569; max-width: auto; margin: 0 auto 60px; line-height: 1.6; }
              .problem-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 24px; padding: 32px 24px; position: relative; overflow: hidden; z-index: 1; transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); height: 100%; 
              box-shadow: 0 2px 0 rgba(0,0,0,0.04); 
              box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 2px solid #e5e7eb;
              }

              .page-section{padding: 70px 0 70px 0;}

              .problem-card:hover { border-color: rgba(6,123,177,0.3); transform: translateY(-2px); box-shadow: 0 20px 40px -12px rgba(6,123,177,0.15); }
              .problem-icon-box { width: 64px; height: 64px; border-radius: 18px; display: flex; align-items: center; justify-content: center; margin-bottom: 24px; position: relative; z-index: 2; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); color: #fff; box-shadow: 0 4px 12px rgba(6,123,177,0.2); border: 1px solid rgba(6,123,177,0.25); transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
              .problem-icon-box svg { transition: transform 0.3s ease; }
              .problem-card:hover .problem-icon-box { transform: scale(1.1); box-shadow: 0 10px 25px rgba(6,123,177,0.4); }
              .problem-card:hover .problem-icon-box svg, .outcome-card:hover .outcome-icon-box svg { transform: scale(1.1) rotate(0deg); }
              .problem-card-title { font-size: 20px; font-weight: 700; color: #0f172a; margin-bottom: 12px; transition: color 0.3s ease; }
              .problem-card:hover .problem-card-title, .outcome-card:hover .fs-5.fw-bold { color: #067bb1; }
              .problem-card-text { font-size: 14px; color: #64748b; line-height: 1.5; margin-bottom: 0; }
              
              /* Updated Top-Right Shape to match screenshot design */
              .shape-tr-circle { 
                  position: absolute; 
                  top: -60px; 
                  right: -60px; 
                  width: 150px; 
                  height: 150px; 
                  
                  border-radius: 50%; 
                  background: transparent;
                  border: 1px solid rgba(6,123,177,0.15);
                  z-index: 0;
                  pointer-events: none;
              }
              .shape-tr-inner {
                  position: absolute;
                  top: -30px;
                  right: -30px;
                  width: 120px;
                  height: 120px;
                  border-radius: 50%;
                  background: radial-gradient(circle at center, rgba(6,123,177,0.04) 0%, transparent 70%);
                  z-index: 0;
              }

              .shape-bl-circle { position: absolute; bottom: 0; left: 0; width: 100px; height: 100px; background: radial-gradient(circle at bottom left, rgba(6,123,177,0.1), transparent 70%); border-top-right-radius: 100%; opacity: 0.6; }
              .shape-diamond { position: absolute; top: 20px; right: 20px; width: 12px; height: 12px; background: #b8daff; transform: rotate(45deg); opacity: 0; transition: all 0.4s ease; }
              .problem-card:hover .shape-diamond, .outcome-card:hover .shape-diamond { opacity: 1; transform: rotate(45deg) translate(10px, -10px); }
              .icon-glow { position: absolute; width: 100%; height: 100%; border-radius: 16px; filter: blur(12px); opacity: 0; transition: opacity 0.5s ease; z-index: -1; background: rgba(6,123,177,0.4); }
              .problem-card:hover .icon-glow, .outcome-card:hover .icon-glow { opacity: 0.6; }
              .mini-badge { position: absolute; bottom: -4px; right: -4px; width: 16px; height: 16px; background: #fff; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transform: scale(0); transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
              .problem-card:hover .mini-badge, .outcome-card:hover .mini-badge { transform: scale(1); }
              .line-top { position: absolute; top: 0; left: 0; width: 100%; height: 3px; background: linear-gradient(90deg, transparent, #067bb1, transparent); opacity: 0; transition: opacity 0.5s ease; }
              .problem-card:hover .line-top, .outcome-card:hover .line-top { opacity: 1; }
              .line-bottom { position: absolute; bottom: 0; left: 0; width: 100%; height: 3px; background: linear-gradient(90deg, transparent, #067bb1, transparent); transform: scaleX(0); transform-origin: left; transition: transform 0.5s ease; }
              .problem-card:hover .line-bottom, .outcome-card:hover .line-bottom { transform: scaleX(1); }
              @keyframes rippleEffect { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(2.5); opacity: 0; } }
              .circle-border-anim { position: absolute; top: 10px; right: 10px; width: 40px; height: 40px; border: 1px solid rgba(6,123,177,0.3); border-radius: 50%; opacity: 0; pointer-events: none; }
              .problem-card:hover .circle-border-anim, .outcome-card:hover .circle-border-anim { animation: rippleEffect 0.8s ease-out; }
              
              /* Zero-CapEx GCC Model Section - Advanced Styling */
              .model-section { padding: 70px 0 70px 0; background: #fff; position: relative; overflow: hidden; }
              .approach-model-section{background: rgb(226, 241, 254) !important;}
              /* Header Styling */
              .model-pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 999px; background: #E2F1FE; color: #067bb1; border: 1px solid #b8daff; font-size: 12px; font-weight: 700; margin-bottom: 24px; }
              .model-title { font-size: 42px; font-weight: 800; color: #000; letter-spacing: -0.02em; margin-bottom: 24px; }
              .model-subtitle { font-size: 18px; color: #475569; max-width: 700px; margin: 0 auto 16px; font-weight: 500; }
              .model-cta-text { font-size: 16px; color: #067bb1; font-weight: 700; margin-bottom: 60px; }
              
              /* Card Container */
              .model-card { 
                position: relative; 
                background: #fff; 
                border-radius: 24px; 
                padding: 40px 32px; 
                border: 1px solid #e2e8f0; 
                height: 100%; 
                overflow: hidden; 
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); /* shadow-lg */
                transition: all 0.3s ease;
                z-index: 1;
              }
              
              /* Card Hover State */
              .model-card:hover { 
                border-color: rgba(6,123,177,0.4); /* border-[#067bb1]/40 */
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); /* shadow-2xl */
              }
              
              /* Top-Right Gradient Corner */
              .model-shape-tr { 
                position: absolute; 
                top: 0; 
                right: 0; 
                width: 120px; 
                height: 120px; 
                background: radial-gradient(circle at top right, rgba(6,123,177,0.15), transparent 70%); 
                border-bottom-left-radius: 100%; /* rounded-bl-full approx */
                z-index: 0;
                opacity: 0.6;
              }
              
              /* Expanding Circle Border (Ripple) */
              .model-ripple-tr {
                position: absolute;
                top: -20px;
                right: -20px;
                width: 140px;
                height: 140px;
                border: 1px solid rgba(6,123,177,0.2);
                border-radius: 50%;
                z-index: 0;
                transform: scale(1);
                opacity: 1;
                transition: transform 0.7s ease, opacity 0.7s ease;
              }
              .model-card:hover .model-ripple-tr {
                transform: scale(1.5);
                opacity: 0;
              }

              /* Bottom-Left Corner Gradient */
              .model-shape-bl { 
                position: absolute; 
                bottom: 0; 
                left: 0; 
                width: 80px; 
                height: 80px; 
                background: radial-gradient(circle at bottom left, rgba(6,123,177,0.1), transparent 70%); 
                border-top-right-radius: 100%; 
                z-index: 0;
              }
              
              /* Top Accent Line */
              .model-line-top {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 3px;
                background: linear-gradient(90deg, #067bb1, #6bb8e0);
                opacity: 0;
                transition: opacity 0.3s ease;
                z-index: 2;
              }
              .model-card:hover .model-line-top {
                opacity: 1;
              }
              
              /* Bottom Shine Effect */
              .model-shine-bottom {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 24px;
                background: linear-gradient(to top, rgba(6,123,177,0.05), transparent);
                opacity: 0;
                transition: opacity 0.3s ease;
                pointer-events: none;
                z-index: 1;
              }
              .model-card:hover .model-shine-bottom {
                opacity: 1;
              }

              /* Icon Box & Glow */
              .model-icon-box { 
                width: 64px; 
                height: 64px; 
                border-radius: 16px; 
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                margin-bottom: 24px; 
                color: white; 
                position: relative; 
                z-index: 2; 
                transition: transform 0.5s ease, background-color 0.3s ease;
             flex-shrink: 0;
                }
              .model-card:hover .model-icon-box {
                transform: scale(1.1) rotate(6deg);
              }
              
              /* Icon Glow Halo */
              .model-icon-glow {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background: rgba(6,123,177,0.6);
                filter: blur(15px);
                opacity: 0.4;
                transition: opacity 0.3s ease;
                z-index: -1;
              }
              .model-card:hover .model-icon-glow {
                opacity: 0.6;
              }

              /* Corner Dots on Icon */
              .model-icon-dot {
                position: absolute;
                width: 4px;
                height: 4px;
                background: white;
                border-radius: 50%;
                opacity: 0;
                transition: opacity 0.3s ease;
              }
              .model-icon-dot.top-left { top: 6px; left: 6px; transition-delay: 0s; }
              .model-icon-dot.bottom-right { bottom: 6px; right: 6px; transition-delay: 0.1s; }
              
              .model-card:hover .model-icon-dot { opacity: 1; }
              
              /* Floating Diamond */
              .model-icon-diamond {
                position: absolute;
                top: -6px;
                right: -6px;
                width: 8px;
                height: 8px;
                background: #6bb8e0;
                transform: rotate(45deg);
                opacity: 0;
                transition: all 0.5s ease;
              }
              .model-card:hover .model-icon-diamond {
                opacity: 1;
                transform: rotate(45deg) translate(2px, -2px);
              }

              /* Content Styling */
              .model-card-title { 
                font-size: 28px; 
                font-weight: 800; 
                color: #0f172a; 
                margin-bottom: 12px; 
                transition: color 0.3s ease; 
              }
              .model-card:hover .model-card-title {
                color: #067bb1;
              }
              /* Header row: icon left, title + time right */
              .model-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
              .model-header .model-icon-box { margin-bottom: 0; }
              .model-header-text { display: flex; flex-direction: column; }
              
              /* Timeline Badge */
              .model-time-pill { 
                display: inline-block; 
                padding: 6px 16px; 
                background: #E2F1FE; 
                color: #028bc6; 
                border-radius: 999px; 
                font-size: 13px; 
                font-weight: 800; 
                margin-bottom: 0; 
              }
              
              /* List Items */
              .model-list-item { 
                display: flex; 
                align-items: center; 
                gap: 12px; 
                margin-bottom: 5px; 
                font-size: 15px; 
                color: #475569; 
                font-weight: 500;
                transition: color 0.3s ease;
              }
              .model-list-item:hover {
                color: #0f172a; /* gray-900 */
              }
                                .testimonials-section {
                 background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
                }
              
              .model-check-icon { 
                width: 24px; 
                height: 24px; 
                background: #E2F1FE; 
                border-radius: 8px; 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                color: #067bb1; 
                flex-shrink: 0;
                transition: background-color 0.3s ease;
              }
              .model-list-item:hover .model-check-icon {
                background: rgba(226, 241, 254, 0.8); /* slightly darker/more opacity */
              }

              /* Arrow Connector */
              .arrow-connector-container {
                display: none;
              }
              @media (min-width: 992px) {
                .arrow-connector-container {
                  display: block;
                  position: absolute;
                  top: 50%;
                  right: -30px;
                  transform: translateY(-50%);
                  width: 60px;
                  height: 20px;
                  z-index: 10;
                  pointer-events: none;
                }
                .arrow-line {
                  width: 100%;
                  height: 2px;
                  background: linear-gradient(90deg, #067bb1, #6bb8e0);
                  position: relative;
                  top: 9px;
                }
                .arrow-head {
                  position: absolute;
                  right: 0;
                  top: 50%;
                  transform: translateY(-50%);
                  width: 0;
                  height: 0;
                  border-top: 6px solid transparent;
                  border-bottom: 6px solid transparent;
                  border-left: 10px solid #6bb8e0;
                }
              }
              
              /* Background Orbs */
              .bg-orb {
                position: absolute;
                border-radius: 50%;
                filter: blur(40px);
                z-index: 0;
                opacity: 0.4;
                animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
              }
              .bg-orb-1 { top: 10%; left: 5%; width: 150px; height: 150px; background: #fee2e2; }
              .bg-orb-2 { bottom: 10%; right: 5%; width: 200px; height: 200px; background: #ffedd5; animation-delay: 1.5s; }
              
              /* Modern Geometric Butterfly Layout */
              .outcomes-butterfly-section {
                padding: 70px 0 40px 0;
                background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
                overflow: hidden;
                position: relative;
              }
              
              .butterfly-container {
                position: relative;
                max-width: 1100px;
                margin: 0 auto;
                min-height: 550px; /* Reduced from 700px */
                display: flex;
                align-items: center;
                justify-content: center;
              }

              /* SVG Connection Layer */
              .butterfly-svg-layer {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 1;
              }

              .connection-path {
                fill: none;
                stroke-width: 2;
                stroke-dasharray: 8, 8;
                opacity: 0.3;
                transition: all 0.5s ease;
              }

              .outcome-item-butterfly:hover ~ .butterfly-svg-layer .connection-path {
                opacity: 0.8;
                stroke-width: 3;
                stroke-dasharray: none;
              }
              
              .butterfly-center {
                position: relative;
                z-index: 10;
                width: 200px; /* Slightly reduced */
                height: 200px; /* Slightly reduced */
                background: #fff;
                border-radius: 50%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                box-shadow: 0 20px 50px rgba(0,0,0,0.08);
                padding: 15px;
              }

              .butterfly-center-inner {
                width: 100%;
                height: 100%;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                border-radius: 50%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                color: #fff;
                padding: 15px;
              }
              
              .butterfly-center-icon {
                font-size: 32px;
                margin-bottom: 8px;
                filter: drop-shadow(0 4px 10px rgba(0,0,0,0.2));
              }
              
              .butterfly-center-title {
                font-size: 18px;
                font-weight: 800;
                line-height: 1.2;
                text-transform: uppercase;
                letter-spacing: 1px;
              }
              
              .butterfly-wing {
                position: absolute;
                width: 50%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: space-between; /* Using space-between for tighter fit */
                padding: 40px 0; /* Reduced padding */
                z-index: 5;
              }
              
              .butterfly-wing-left {
                left: 0;
                align-items: flex-end;
                padding-right: 120px;
              }
              
              .butterfly-wing-right {
                right: 0;
                align-items: flex-start;
                padding-left: 120px;
              }
              
              .outcome-item-butterfly {
                position: relative;
                width: 280px;
                height: 85px; /* Fixed height for all cards */
                transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                cursor: pointer;
              }
              
              .outcome-item-butterfly:hover {
                transform: translateY(-3px) scale(1.02);
              }
              
              .outcome-card-butterfly {
                padding: 0 25px; /* Removed vertical padding */
                height: 100%; /* Fill fixed item height */
                background: #e2f1fe;
                border-radius: 20px;
                color: #1e293b;
                font-size: 14px; /* Slightly smaller text */
                font-weight: 700;
                line-height: 1.3;
                box-shadow: 0 10px 25px rgba(0,0,0,0.05);
                border: 1px solid rgba(0,0,0,0.05);
                display: flex;
                align-items: center;
                gap: 12px;
              }

              .outcome-dot {
                width: 10px;
                height: 10px;
                border-radius: 50%;
                flex-shrink: 0;
              }
              
              .butterfly-wing-left .outcome-card-butterfly {
                flex-direction: row-reverse;
                text-align: right;
              }
              
              .outcome-number-badge {
                position: absolute;
                top: -15px; /* Adjusted for icons */
                width: 35px;
                height: 35px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                z-index: 10;
                border: 2px solid #fff;
              }

              .butterfly-wing-left .outcome-number-badge { right: 15px; }
              .butterfly-wing-right .outcome-number-badge { left: 15px; }

              @media (max-width: 991px) {
                .butterfly-container {
                  flex-direction: column;
                  min-height: auto;
                  gap: 40px;
                  padding-top: 20px;
                }
                .butterfly-svg-layer { display: none; }
                .butterfly-wing {
                  position: relative;
                  width: 100%;
                  height: auto;
                  padding: 0;
                  gap: 20px;
                }
                .butterfly-wing-left, .butterfly-wing-right {
                  align-items: center;
                  padding: 0;
                }
                .outcome-item-butterfly { width: 90%; max-width: 350px; }
                .butterfly-wing-left .outcome-card-butterfly { flex-direction: row; text-align: left; }
                .butterfly-wing-left .outcome-number-badge { left: 20px; right: auto; }
              }
              
              @keyframes pulse {
                0%, 100% { opacity: 0.4; transform: scale(1); }
                50% { opacity: 0.6; transform: scale(1.1); }
              }

              /* High-Value GCC Pods Section - Advanced Effects */
              .gcc-pods-section {
                padding: 80px 0 70px 0;
                background: #effdff;
                position: relative;
                overflow: hidden;
              }
              
              /* 1. Grid Pattern */
              .gcc-pods-grid-bg {
                position: absolute;
                inset: 0;
                background-image: 
                  linear-gradient(to right, rgba(128, 128, 128, 0.12) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(128, 128, 128, 0.12) 1px, transparent 1px);
                background-size: 24px 24px;
                mask-image: radial-gradient(ellipse at center, black 40%, transparent 80%);
                z-index: 0;
                pointer-events: none;
                display: none;
              }

              /* 2. Animated Pulsing Orbs */
              .pod-orb {
                position: absolute;
                border-radius: 50%;
                filter: blur(120px);
                z-index: 0;
                animation: podPulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
              }
              .pod-orb-tl {
                top: -100px;
                left: -100px;
                width: 384px;
                height: 384px;
                // background: rgba(0, 126, 238, 0.2);
              }
              .pod-orb-br {
                bottom: -100px;
                right: -100px;
                width: 384px;
                height: 384px;
                // background: rgba(238, 0, 0, 0.1);
                animation-delay: 2s;
              }
              @keyframes podPulse {
                0%, 100% { opacity: 1; }
                50% { opacity: .5; }
              }

              /* 3. Badge with Pulsing Dot */
              .pod-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
                border-radius: 9999px;
                background: rgba(255, 255, 255, 0.03);
                border: 1px solid rgba(6,123,177,0.2);
                backdrop-filter: blur(4px);
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                text-transform: uppercase;
              }
              .pod-badge-dot {
                position: relative;
                width: 6px;
                height: 6px;
                background-color: #067bb1;
                border-radius: 50%;
              }
              .pod-badge-dot::after {
                content: '';
                position: absolute;
                inset: -4px;
                border-radius: 50%;
                background-color: #067bb1;
                opacity: 0.75;
                animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
              }
              @keyframes ping {
                75%, 100% { transform: scale(2); opacity: 0; }
              }

              .gcc-pods-title {
                color: #000;
                font-size: 42px; /* 6xl approx */
                font-weight: 800;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
                line-height: 1.1;
              }
              .gcc-pods-subtitle {
                font-size: 18px;
                max-width: 600px;
                margin: 0 auto 60px;
                line-height: 1.625;
              }

              /* 6. Card Background & Design */
              .pod-dark-card {
                position: relative;
                height: 100%;
                background: linear-gradient(135deg, #067bb1 0%, #04467a 100%); /* zinc-900 to black */
                border: 1px solid #067bb1 ; /* zinc-800 */
                border-radius: 24px; /* 3xl */
                padding: 40px 32px;
                overflow: hidden;
                transition: border-color 0.5s ease, transform 0.5s ease;
                z-index: 1;
              }
              .pod-dark-card:hover {
                border-color: #067bb1 ;
                transform: translateY(-5px);
              }

              /* 5. Outer Glow Effect */
              .pod-card-glow {
                position: absolute;
                inset: -1px;
                // background: linear-gradient(to bottom right, #ee0000, #7f1d1d);
                opacity: 0;
                filter: blur(24px); /* 2xl */
                transition: opacity 0.7s ease;
                z-index: -1;
                border-radius: 24px;
              }
              .pod-dark-card:hover .pod-card-glow {
                opacity: 0.2;
              }

              /* 7. Full-Card Shine Overlay */
              .pod-shine-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 100%);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .pod-dark-card:hover .pod-shine-overlay {
                opacity: 1;
              }

              /* 8. Top Accent Line */
              .pod-line-top {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                box-shadow: 0 0 4px #067bb1;
              }
              .pod-dark-card:hover .pod-line-top {
                opacity: 1;
              }

              /* 9. Icon Container & Glow */
              .pod-icon-wrapper {
                position: relative;
                width: 64px;
                height: 64px;
                border-radius: 16px; /* slightly rounded square */
                background: linear-gradient(135deg, #067bb1, #04467a);
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 32px;
                box-shadow: 0 10px 15px -3px rgba(6,123,177,0.3);
                transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                z-index: 2;
              }
              .pod-dark-card:hover .pod-icon-wrapper {
                transform: scale(1.1);
              }
              .pod-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(24px); /* xl blur */
                opacity: 0.5;
                transition: opacity 0.5s ease;
                z-index: -1;
                border-radius: 50%;
              }
              .pod-dark-card:hover .pod-icon-glow {
                opacity: 1;
              }
              .pod-icon-wrapper svg {
                color: #fff;
                z-index: 3;
              }

              /* 10. Decorative Dots */
              .pod-card-dots {
                position: absolute;
                top: 24px;
                right: 24px;
                display: flex;
                gap: 2px; /* 1.5px approx */
              }
              .pod-dot {
                width: 3px;
                height: 3px;
                background-color: #3f3f46; /* zinc-700 */
                border-radius: 50%;
              }
              .pod-dot.accent {
                background-color: #067bb1;
              }

              /* 11. Title Color Shift */
              .pod-card-title {
                color: #fff;
                font-size: 24px;
                font-weight: 700;
                margin-bottom: 24px;
                transition: color 0.3s ease;
              }
              .pod-dark-card:hover .pod-card-title {
                // color: #ee0000;
              }

              /* 12. Capability List Items */
              .pod-feature-list {
                list-style: none;
                padding: 0;
                margin: 0 0 40px 0;
              }
              .pod-feature-item {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 16px;
                color: #fff; /* gray-400 */
                font-size: 14px;
                line-height: 1.6;
                transition: color 0.3s ease;
                cursor: default;
              }
              .pod-feature-item:hover {
                color: #fff;
              }
              .pod-bullet {
                width: 4px;
                height: 4px;
                background-color: #067bb1;
                border-radius: 50%;
                margin-top: 9px;
                flex-shrink: 0;
                transition: all 0.3s ease;
              }
              .pod-feature-item:hover .pod-bullet {
                transform: scale(1.5);
                box-shadow: 0 0 4px #067bb1;
              }

              /* 13. Outcome Section */
              .pod-outcome-box {
                border-top: 1px solid #27272a; /* zinc-800 */
                padding-top: 24px;
                transition: border-color 0.5s ease;
                position: relative;
                z-index: 2;
              }
              .pod-dark-card:hover .pod-outcome-box {
                border-color: rgba(6,123,177,0.3);
              }
              .pod-outcome-label {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                background: rgba(6,123,177,0.1);
                border-radius: 6px;
                color: #fff;
                font-size: 11px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-bottom: 12px;
              }
              .pod-outcome-dot {
                width: 4px;
                height: 4px;
                background-color: #067bb1;
                border-radius: 50%;
              }
              .pod-outcome-text {
                color: #d1d5db; /* gray-300 */
                font-size: 13px;
                line-height: 1.5;
              }

              /* 14. Bottom Gradient Glow */
              .pod-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 128px;
                background: linear-gradient(to top, rgba(6,123,177,0.05), transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
                z-index: 0;
              }
              .pod-dark-card:hover .pod-bottom-glow {
                opacity: 1;
              }

              /* Background Glow Effect */
              .section-glow-top {
                position: absolute;
                top: 0;
                left: 50%;
                transform: translateX(-50%);
                width: 600px;
                height: 300px;
                // background: radial-gradient(ellipse at center, rgba(238, 0, 0, 0.15), transparent 70%);
                pointer-events: none;
                z-index: 1;
              }

              /* Prime + Partner Model Section */
              .prime-partner-section {
                padding: 70px 0 80px 0;
                background-color: #fff;
                position: relative;
                overflow: hidden;
              }
              
              /* 1. Pulsing Ambient Orbs */
              .prime-orb-large {
                position: absolute;
                width: 600px;
                height: 600px;
                background: radial-gradient(circle, rgba(6,123,177,0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(150px);
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 0;
                animation: primePulse 4s ease-in-out infinite;
              }
              .prime-orb-small {
                position: absolute;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(6,123,177,0.03) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(120px);
                top: 0;
                right: 0;
                z-index: 0;
                animation: primePulse 5s ease-in-out infinite 1.5s;
              }
              @keyframes primePulse {
                0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
                50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
              }
              
              /* Header Badge */
              .prime-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background: rgba(255, 255, 255, 0.8);
                backdrop-filter: blur(4px);
                border: 1px solid rgba(6,123,177,0.2);
                border-radius: 9999px;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                margin-bottom: 24px;
              }
              .prime-badge-dot {
                width: 8px;
                height: 8px;
                background-color: #067bb1;
                border-radius: 50%;
                position: relative;
              }
              .prime-badge-dot::after {
                content: '';
                position: absolute;
                top: -4px;
                left: -4px;
                right: -4px;
                bottom: -4px;
                background-color: #067bb1;
                border-radius: 50%;
                opacity: 0.4;
                animation: primePing 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
              }
              @keyframes primePing {
                75%, 100% { transform: scale(2); opacity: 0; }
              }
              .prime-badge-text {
                font-size: 14px;
                font-weight: 600;
                color: #334155;
                letter-spacing: 0.025em;
              }

              /* Typography */
              .prime-partner-title {
                color: #000;
                font-size: 42px;
                font-weight: 800;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
                line-height: 1.1;
              }
              .prime-partner-subtitle {
               font-size: 18px;
  color: #475569;
  max-width: 700px;
  margin: 0 auto 16px;
    margin-bottom: 16px;
  font-weight: 500;
              }

              /* Card Styling */
              .prime-partner-card {
                background: #fff;
                border-radius: 24px;
                padding: 40px;
                height: 100%;
                position: relative;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                border: 2px solid #e5e7eb;
                transition: all 0.5s ease;
                z-index: 1;
                overflow: hidden;
              }
              /* 7. Main Card Hover */
              .group:hover .prime-partner-card {
                border-color: rgba(6,123,177,0.5);
                box-shadow: 0 25px 50px -12px rgba(6,123,177,0.15);
                transform: translateY(-5px);
              }

              /* 6. Outer Glow Halo */
              .prime-card-halo {
                position: absolute;
                inset: -1px;
                // background: linear-gradient(135deg, #067bb1, #04467a);
                opacity: 0;
                filter: blur(24px);
                border-radius: 24px;
                z-index: -1;
                transition: opacity 0.7s ease;
              }
              .group:hover .prime-card-halo {
                opacity: 0.2;
              }

              /* 8. Diagonal Shine Overlay */
              .prime-shine-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(135deg, rgba(6,123,177,0.05) 0%, transparent 60%);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .group:hover .prime-shine-overlay {
                opacity: 1;
              }

              /* 9. Top Accent Line */
              .prime-top-line {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .group:hover .prime-top-line {
                opacity: 1;
              }

              /* 10. Decorative Corner Shape */
              .prime-corner-shape {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 20px;
                height: 20px;
                border-top: 2px solid rgba(6,123,177,0.1);
                border-right: 2px solid rgba(6,123,177,0.1);
                border-top-right-radius: 12px;
                transition: all 0.5s ease;
              }
              .group:hover .prime-corner-shape {
                border-color: rgba(6,123,177,0.3);
                transform: translate(2px, -2px);
              }

              /* 11. Icon Glow */
              .prime-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(20px);
                opacity: 0.3;
                border-radius: 50%;
                z-index: -1;
                transition: opacity 0.5s ease;
              }
              .group:hover .prime-icon-glow {
                opacity: 0.6;
              }

              /* 12. Icon Container */
              .prime-card-header-row {
                display: flex;
                align-items: center;
                gap: 20px;
                margin-bottom: 32px;
              }
              .prime-header-text-col {
                display: flex;
                flex-direction: column;
                justify-content: center;
              }

              .prime-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                transition: transform 0.5s ease;
                box-shadow: 0 4px 12px rgba(6, 123, 177, 0.2);
                flex-shrink: 0;
              }
              .group:hover .prime-icon-box {
                transform: scale(1.1);
              }
              .prime-icon-box.red {
                background: linear-gradient(135deg, #067bb1, #04467a);
                color: #fff;
              }
              .prime-icon-box.dark-red {
                background: linear-gradient(135deg, #067bb1, #04467a);
                color: #fff;
              }

              /* 13. Company Name */
              .prime-card-brand {
                font-size: 28px;
                font-weight: 800;
                color: #000;
                margin: 0 0 4px 0;
                transition: color 0.3s ease;
                line-height: 1;
              }
              .group:hover .prime-card-brand {
                color: #067bb1;
              }

              /* 14. Role Label */
              .prime-role-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: 700;
                font-size: 13px;
                letter-spacing: 0.05em;
                text-transform: uppercase;
              }
              .prime-role-line {
                width: 24px;
                height: 2px;
              }
              .prime-role-text {
                transition: color 0.3s ease;
              }
              /* Comsense specifics */
              .prime-role-comsense .prime-role-line { background-color:#067bb1; }
              .prime-role-comsense .prime-role-text { color: #028bc6 }
              /* SRKAY specifics */
              .prime-role-srkay .prime-role-line { background-color:#067bb1; }
              .prime-role-srkay .prime-role-text { color: #028bc6; }

              /* List Items */
              .prime-list {
                list-style: none;
                padding: 0;
                margin: 0;
              }
              .prime-list-item {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 20px;
                color: #374151;
                font-weight: 500;
                font-size: 15px;
                line-height: 1.5;
                transition: color 0.3s ease;
                position: relative;
              }
              .prime-list-item:hover {
                color: #000;
              }

              /* 15. Bullet Point Animations */
              .prime-bullet-container {
                position: relative;
                width: 12px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              }
              .prime-bullet {
                width: 4px; /* Base size */
                height: 4px;
                background-color: #067BB1;
                border-radius: 50%;
                transition: all 0.3s ease;
              }
              .prime-list-item:hover .prime-bullet {
                transform: scale(1.5) translateY(1px); /* 1.5px -> 2px roughly */
              }
              .prime-bullet-glow {
                position: absolute;
                width: 12px;
                height: 12px;
                background-color: #CDE4ED;
                border-radius: 50%;
                filter: blur(4px);
                opacity: 0;
                transition: opacity 0.3s ease;
                top: 6px;
                left: 0;
              }
              .prime-list-item:hover .prime-bullet-glow {
                opacity: 1;
              }

              /* Bottom Glow Effect */
              // .prime-bottom-glow {
              //   position: absolute;
              //   bottom: 0;
              //   left: 0;
              //   right: 0;
              //   height: 128px;
              //   background: linear-gradient(to top, rgba(227, 24, 55, 0.05), transparent);
              //   opacity: 0;
              //   transition: opacity 0.5s ease;
              //   pointer-events: none;
              // }
              .group:hover .prime-bottom-glow {
                opacity: 1;
              }

              /* Proven Outcomes Section */
              .proven-outcomes-section {
                padding: 70px 0 80px 0;
                background-color: #e2f1fe; /* gray-50 */
                position: relative;
                overflow: hidden;
              }
              
              .outcome-title {
                color: #000;
                font-size: 42px; /* 6xl */
                font-weight: 800;
                margin-bottom: 24px;
                letter-spacing: -0.02em;
                line-height: 1.1;
              }

              /* Orbs */
              .outcome-orb-left {
                position: absolute;
                width: 500px;
                height: 500px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(150px);
                top: 40%;
                left: -100px;
                z-index: 0;
                animation: primePulse 4s ease-in-out infinite;
              }
              .outcome-orb-right {
                position: absolute;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.03) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(120px);
                top: 10%;
                right: -50px;
                z-index: 0;
                animation: primePulse 5s ease-in-out infinite 1.5s;
              }

              /* Card Styles */
              .outcome-card {
                background: #fff;
                border-radius: 24px;
                padding: 24px 24px;
                height: 100%;
                position: relative;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                border: 2px solid #e6f0ff;
                transition: all 0.5s ease;
                z-index: 1;
                overflow: hidden;
              }
              .group:hover .outcome-card {
                border-color: rgba(6, 123, 177, 0.5);
                box-shadow: 0 25px 50px -12px rgba(6, 123, 177, 0.15);
                transform: translateY(-5px);
              }

              /* Decorative Elements */
              .outcome-card-halo {
                position: absolute;
                inset: -1px;
                background: 0 20px 60px -10px rgba(6, 123, 177, 0.1);
                opacity: 0;
                filter: blur(24px);
                border-radius: 24px;
                z-index: -1;
                transition: opacity 0.7s ease;
              }
              .group:hover .outcome-card-halo { opacity: 0.2; }

              .outcome-shine-overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(135deg, rgba(6, 123, 177, 0.05) 0%, transparent 60%);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .group:hover .outcome-shine-overlay { opacity: 1; }

              .outcome-top-line {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .group:hover .outcome-top-line { opacity: 1; }

              .outcome-corner-shape {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 20px;
                height: 20px;
                border-top: 2px solid rgba(6, 123, 177, 0.1);
                border-right: 2px solid rgba(6, 123, 177, 0.1);
                border-top-right-radius: 12px;
                transition: all 0.5s ease;
              }
              .group:hover .outcome-corner-shape {
                border-color: rgba(6, 123, 177, 0.3);
                transform: translate(2px, -2px);
              }

              .outcome-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 128px;
                background: linear-gradient(to top, rgba(6, 123, 177, 0.05), transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                pointer-events: none;
              }
              .group:hover .outcome-bottom-glow { opacity: 1; }

              /* Header Row */
              .outcome-header-row {
                display: flex;
                align-items: center;
                gap: 20px;
                margin-bottom: 32px;
              }
              .outcome-header-text {
                display: flex;
                flex-direction: column;
                justify-content: center;
                flex: 1;
              }

              /* Icon Box */
              .outcome-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                transition: transform 0.5s ease;
                box-shadow: 0 10px 15px -3px rgba(6,123,177,0.3);
                flex-shrink: 0;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                color: #fff;
              }
              .group:hover .outcome-icon-box { transform: scale(1.1); }
              
              .outcome-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(20px);
                opacity: 0.3;
                border-radius: 50%;
                z-index: -1;
                transition: opacity 0.5s ease;
              }
              .group:hover .outcome-icon-glow { opacity: 0.6; }

              /* Title & Role */
              .outcome-card-title {
                font-size: 28px;
                font-weight: 800;
                color: #000;
                margin: 0 0 4px 0;
                transition: color 0.3s ease;
                line-height: 1.2;
              }
              .group:hover .outcome-card-title { color: #067bb1; }

              .outcome-role-label {
                display: flex;
                align-items: center;
                gap: 8px;
                font-weight: 700;
                font-size: 13px;
                letter-spacing: 0.05em;
                text-transform: uppercase;
              }
              .outcome-role-line {
                width: 24px;
                height: 2px;
                background-color: #067bb1;
              }
              .outcome-role-text {
                color: #067bb1;
              }

              /* Metric Rows (Micro-Interactions) */
              .outcome-metrics {
                display: flex;
                flex-direction: column;
                gap: 16px;
              }
              .outcome-metric-row {
                display: flex;
                align-items: center;
                gap: 16px;
                padding: 16px;
                background-color: #f9fafb; /* gray-50 */
                border: 1px solid transparent;
                border-radius: 12px;
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
              }
              .outcome-metric-row:hover {
                background-color: #fff;
                border-color: rgba(6, 123, 177, 0.3);
                box-shadow: 0 0 20px rgba(6, 123, 177, 0.1);
              }

              /* Metric Icon */
              .outcome-metric-icon {
                width: 40px;
                height: 40px;
                border-radius: 10px;
                background-color: #fff;
                border: 1px solid #e5e7eb;
                color: #6b7280;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.3s ease;
              }
              .outcome-metric-row:hover .outcome-metric-icon {
                background-color: #eff6ff;
                border-color: #bfdbfe;
                color: #067bb1;
                transform: scale(1.1);
                box-shadow: 0 0 10px rgba(6, 123, 177, 0.2);
              }

              /* Metric Text */
              .outcome-metric-label {
                flex-grow: 1;
                font-size: 15px;
                font-weight: 500;
                color: #4b5563;
                transition: color 0.3s ease;
              }
              .outcome-metric-row:hover .outcome-metric-label {
                color: #111827;
              }

              .outcome-metric-value {
                font-size: 16px;
                font-weight: 700;
                color: #067bb1;
                transition: transform 0.3s ease;
              }
              .outcome-metric-row:hover .outcome-metric-value {
                transform: scale(1.1);
              }

              /* 90-Day Launch Sprint Section */
              .launch-sprint-section {
                padding: 70px 0 80px 0;
                background-color: #fff; /* gray-50 */
                position: relative;
                overflow: hidden;
              }

              .launch-sprint-section.faq-section {
                padding: 70px 0 80px 0;
                background-color: #fff; /* gray-50 */
                position: relative;
                overflow: hidden;
              }
              
              /* Background Orbs */
              // .launch-bg-orb-large {
              //   position: absolute;
              //   top: 10%;
              //   left: -10%;
              //   width: 500px;
              //   height: 500px;
              //   background: radial-gradient(circle, rgba(227, 24, 55, 0.08) 0%, transparent 70%);
              //   border-radius: 50%;
              //   filter: blur(60px);
              //   animation: pulseGlow 8s infinite ease-in-out;
              //   z-index: 0;
              // }
              .launch-bg-orb-small {
                position: absolute;
                bottom: 10%;
                right: -5%;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.08) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(50px);
                animation: pulseGlow 6s infinite ease-in-out reverse;
                z-index: 0;
              }

              /* Container Design */
              .launch-container {
                position: relative;
                background: linear-gradient(180deg, #effdff 0%, #effdff 100%); /* zinc-900 to black */
                border-radius: 24px;
                padding: 60px 40px;
border: 1px solid rgba(56, 164, 229, 0.5);
  box-shadow: 0 0 0 1px rgba(24, 170, 227, 0.2), 0 20px 50px -12px rgba(24, 132, 227, 0.25);
                overflow: hidden;
              }
              
              .launch-grid-bg {
                position: absolute;
                inset: 0;
                background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                                  linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
                background-size: 24px 24px;
                opacity: 0.3;
                z-index: 0;
                pointer-events: none;
              }
              
              .launch-border-glow {
                position: absolute;
                inset: -20px;
                border-radius: 24px;
                // background: radial-gradient(circle at 50% 0%, rgba(227, 24, 55, 0.15), transparent 70%);
                z-index: 0;
                pointer-events: none;
              }
              
              /* Internal Pulsing Orbs */
              .launch-orb-1 {
                position: absolute;
                top: 20%;
                left: 20%;
                width: 300px;
                height: 300px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.1) 0%, transparent 70%);
                filter: blur(40px);
                animation: pulseGlow 10s infinite ease-in-out;
                z-index: 0;
              }
              .launch-orb-2 {
                position: absolute;
                bottom: 20%;
                right: 20%;
                width: 250px;
                height: 250px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.08) 0%, transparent 70%);
                filter: blur(40px);
                animation: pulseGlow 12s infinite ease-in-out reverse;
                z-index: 0;
              }
              
              .launch-top-accent {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, rgb(221, 244, 255), transparent);
                box-shadow: 0 1px 10px rgb(239, 253, 255);
                z-index: 2;
              }
              
              .launch-corner-tr {
                position: absolute;
                top: 20px;
                right: 20px;
                width: 40px;
                height: 40px;
border-top: 2px solid rgb(221, 244, 255);
  border-right: 2px solid rgb(221, 244, 255);
                border-top-right-radius: 8px;
                z-index: 2;
              }
              
              .launch-corner-bl {
                position: absolute;
                bottom: 20px;
                left: 20px;
                width: 40px;
                height: 40px;
                border-bottom: 2px solid rgb(221, 244, 255);
                border-left: 2px solid rgb(221, 244, 255);
                border-bottom-left-radius: 8px;
                z-index: 2;
              }
              
              .launch-content {
                position: relative;
                z-index: 1;
              }

              /* Header */
              .launch-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
             background: rgba(72, 167, 251, 0.1);
  border: 1px solid rgba(24, 122, 227, 0.3);
  border-radius: 999px;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
              }
              
              .launch-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #067bb1;
                border-radius: 50%;
                box-shadow: 0 0 8px #067bb1;
                animation: pulseGlow 2s infinite;
              }
              
              .launch-title {
                font-size: 42px;
                font-weight: 800;
                color: #000;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
              }
              
              .launch-subtitle {
                font-size: 18px;
                max-width: 600px;
                margin: 0 auto;
              }

              /* Card Styles */
              .launch-card {
                background: rgba(255, 255, 255, 0.03);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 16px;
                padding: 32px;
                position: relative;
                overflow: hidden;
                backdrop-filter: blur(10px);
                transition: all 0.5s ease;
                background: linear-gradient(135deg, #067bb1 0%, #04467a 100%);
  border: 1px solid #067bb1;
              }
              
              .launch-card:hover {
                // border-color: rgba(227, 24, 55, 0.5);
                transform: translateY(-4px);
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
              }
              
              .launch-card-halo {
                position: absolute;
                inset: -2px;
                border-radius: 18px;
                // background: radial-gradient(circle at 50% 0%, rgba(227, 24, 55, 0.3), transparent 70%);
                opacity: 0;
                transition: opacity 0.7s ease;
                z-index: 0;
                pointer-events: none;
              }
              .launch-card:hover .launch-card-halo {
                opacity: 1;
              }
              
              .launch-card-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(255, 255, 255, 0.05) 45%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.05) 55%, transparent 60%);
                transition: left 0.5s ease;
                z-index: 0;
                pointer-events: none;
                opacity: 0;
              }
              .launch-card:hover .launch-card-shine {
                left: 100%;
                opacity: 1;
                transition: left 0.7s ease;
              }

              /* Left Card Specifics */
              .launch-icon-wrapper {
                position: relative;
                width: 64px;
                height: 64px;
                /* margin: 0 auto; Removed for left alignment */
              }
              
              .launch-icon-box {
                width: 100%;
                height: 100%;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                border: 1px solid rgba(255, 255, 255, 0.1);
                border-radius: 20px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                position: relative;
                z-index: 2;
                transition: all 0.5s ease;
              }
              .launch-card:hover .launch-icon-box {
                transform: scale(1.1);
                border-color: #effdff;
                color: #effdff;
              }
              
              .launch-icon-glow {
                position: absolute;
                inset: 0;
                background: #067bb1;
                filter: blur(20px);
                opacity: 0.5;
                transition: opacity 0.5s ease, filter 0.5s ease;
                z-index: 1;
              }
              .launch-card:hover .launch-icon-glow {
                opacity: 1;
                filter: blur(25px);
              }
              
              .launch-days-number {
                font-size: 40px;
                font-weight: 800;
                color: #fff;
                line-height: 1;
                margin-bottom: 0;
                transition: color 0.3s ease;
              }
              .launch-card:hover .launch-days-number {
                color: #fff;
              }
              
              .launch-days-label {
                font-size: 16px;
                color: #fff;
                font-weight: 500;
                transition: color 0.3s ease;
              }
              .launch-card:hover .launch-days-label {
                color: #fff;
              }
              
              .launch-divider {
                width: 100%;
                height: 1px;
                background: rgba(255, 255, 255, 0.15);
                margin: 24px 0;
                transition: background-color 0.3s ease;
              }
              .launch-card:hover .launch-divider {
                background: #effdff;
              }
              
              .launch-days-desc {
                font-size: 18px;
                font-weight: 600;
                color: #fff;
              }
              
              .launch-card-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 2px;
linear-gradient(90deg, transparent, #EDFBFD, transparent)
                opacity: 0;
                transition: opacity 0.5s ease;
                box-shadow: 0 -5px 15px rgba(227, 24, 55, 0.3);
              }
              .launch-card:hover .launch-card-bottom-glow {
                opacity: 1;
              }
              
              .launch-foundation-text {
                font-size: 14px;
                color: #fff !important; /* zinc-500 */
              }

              /* Right Card Specifics */
              .launch-deliverables-title {
                font-size: 24px;
                font-weight: 700;
                color: #fff;
                transition: color 0.3s ease;
              }
              .launch-card:hover .launch-deliverables-title {
                color: #fff;
              }
              
              .launch-list {
                list-style: none;
                padding: 0;
                margin: 0;
              }
              
              .launch-list-item {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                margin-bottom: 0px;
                padding: 8px;
                border-radius: 8px;
                transition: background-color 0.3s ease;
              }
              .launch-list-item:hover {
                background: rgba(255, 255, 255, 0.05);
              }
              
              .launch-check-icon {
                position: relative;
                color: #fff;
                flex-shrink: 0;
                margin-top: 2px;
                transition: transform 0.3s ease;
              }
              .launch-list-item:hover .launch-check-icon {
                transform: scale(1.2);
              }
              
              .launch-check-glow {
                position: absolute;
                inset: 0;
                background: #066EA3;
                filter: blur(8px);
                opacity: 0;
                transition: opacity 0.3s ease;
                border-radius: 50%;
                z-index: -1;
              }
              .launch-list-item:hover .launch-check-glow {
                opacity: 0.6;
              }
              
              .launch-list-text {
                font-size: 15px;
                color: #fff; /* zinc-300 */
                line-height: 1.5;
                transition: color 0.3s ease;
              }
              .launch-list-item:hover .launch-list-text {
                color: #fff;
              }

              /* Why Enterprises Choose Comsense Section */
              .why-section {
                padding: 70px 0 60px 0;
                background-color: #e2f1fe; /* gray-50 */
                position: relative;
                overflow: hidden;
              }
              
              .why-orb-1 {
                position: absolute;
                top: 5%;
                left: -10%;
                width: 500px;
                height: 500px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(60px);
                animation: pulseGlow 8s infinite ease-in-out;
                z-index: 0;
              }
              
              .why-orb-2 {
                position: absolute;
                bottom: 10%;
                right: -5%;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(50px);
                animation: pulseGlow 10s infinite ease-in-out reverse;
                z-index: 0;
              }
              
              .why-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
                background: #fff;
                border: 1px solid rgba(6, 123, 177, 0.2);
                border-radius: 999px;
                color: #028bc6;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                text-transform: uppercase;
                box-shadow: 0 2px 10px rgba(6, 123, 177, 0.08);
              }
              
              .why-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #067bb1;
                border-radius: 50%;
                box-shadow: 0 0 8px rgba(6, 123, 177, 0.6);
                animation: pulseGlow 2s infinite;
              }
              
              .why-title {
                font-size: 42px;
                font-weight: 800;
                color: #111827; /* gray-900 */
                letter-spacing: -0.02em;
                margin-bottom: 16px;
              }
              
              .why-subtitle {
                font-size: 18px;
                color: #6b7280; /* gray-500 */
                max-width: 600px;
                margin: 0 auto;
                line-height: 1.6;
              }
              
              .why-card {
                background: #fff;
                border: 1px solid #e5e7eb; /* gray-200 */
                border-radius: 24px;
                padding: 40px 32px;
                position: relative;
                overflow: hidden;
                transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                height: 100%;
                z-index: 1;
                box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 2px solid #e5e7eb;
              }
              
              .why-card:hover {
                border-color: rgba(6, 123, 177, 0.3);
                transform: translateY(-2px);
                box-shadow: 0 20px 40px -12px rgba(6, 123, 177, 0.15);
              }
              
              .why-card-halo {
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                // background: radial-gradient(circle at 50% 0%, rgba(6, 123, 177, 0.2), transparent 70%);
                opacity: 0;
                transition: opacity 0.7s ease;
                z-index: -1;
                pointer-events: none;
              }
              .why-card:hover .why-card-halo {
                opacity: 1;
              }
              
              .why-card-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(6, 123, 177, 0.03) 45%, rgba(6, 123, 177, 0.06) 50%, rgba(6, 123, 177, 0.03) 55%, transparent 60%);
                transition: left 0.5s ease;
                z-index: 0;
                pointer-events: none;
                opacity: 0;
              }
              .why-card:hover .why-card-shine {
                left: 100%;
                opacity: 1;
              }
              
              .why-accent-line {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
                z-index: 2;
              }
              .why-card:hover .why-accent-line {
                opacity: 1;
              }
              
              .why-corner-l {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 50px;
                height: 50px;
                border-top: 2px solid rgba(6, 123, 177, 0.2);
                border-right: 2px solid rgba(6, 123, 177, 0.2);
                border-top-right-radius: 20px;
                opacity: 1;
                transition: border-color 0.3s ease;
                z-index: 2;
                pointer-events: none;
              }
              .why-card:hover .why-corner-l {
                border-color: rgba(6, 123, 177, 0.6);
              }
              
              .why-corner-orb {
                position: absolute;
                top: -20px;
                right: -20px;
                width: 100px;
                height: 100px;
                background: radial-gradient(circle, rgba(6, 123, 177, 0.15), transparent 70%);
                border-radius: 50%;
                opacity: 0;
                transition: opacity 0.5s ease;
                z-index: 0;
              }
              .why-card:hover .why-corner-orb {
                opacity: 1;
              }
              
              .why-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, #067bb1, transparent);
                box-shadow: 0 -10px 30px rgba(6, 123, 177, 0.15);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .why-card:hover .why-bottom-glow {
                opacity: 1;
              }
              
              .why-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                box-shadow: 0 4px 12px rgba(6, 123, 177, 0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 24px;
                color: #fff;
                position: relative;
                z-index: 2;
                transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
                border: 1px solid rgba(6, 123, 177, 0.25);
              }
              .why-card:hover .why-icon-box {
                transform: scale(1.1);
                box-shadow: 0 10px 25px rgba(6, 123, 177, 0.4);
              }
              
              .why-icon-glow {
                position: absolute;
                inset: 0;
                background: rgba(6,123,177,0.4);
                filter: blur(15px);
                opacity: 0;
                transition: opacity 0.5s ease;
                z-index: -1;
                border-radius: 16px;
              }
              .why-card:hover .why-icon-glow {
                opacity: 0.6;
              }
              
              .why-card-title {
                font-size: 20px;
                font-weight: 700;
                color: #111827; /* gray-900 */
                margin-bottom: 12px;
                transition: color 0.3s ease;
              }
              .why-card:hover .why-card-title {
                color: #067bb1;
              }
              
              .why-card-desc {
                font-size: 15px;
                color: #6b7280; /* gray-500 */
                line-height: 1.6;
                transition: color 0.3s ease;
                margin-bottom: 0px;
              }
              .why-card:hover .why-card-desc {
                color: #1f2937; /* gray-800 */
              }

              /* Connect With Us Section */
              .connect-section {
                padding: 20px 0 100px 0;
                background-color: #ffffff;
                position: relative;
                overflow: hidden;
              }

              .connect-orb-1 {
                position: absolute;
                top: 50%;
                left: 10%;
                width: 500px;
                height: 500px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(60px);
                animation: pulseGlow 8s infinite ease-in-out;
                z-index: 0;
                transform: translateY(-50%);
              }

              .connect-orb-2 {
                position: absolute;
                top: 50%;
                right: 10%;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.05) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(50px);
                animation: pulseGlow 10s infinite ease-in-out reverse;
                z-index: 0;
                transform: translateY(-50%);
              }

              .connect-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 6px 16px;
                background: #fff;
                border: 1px solid rgba(227, 24, 55, 0.2);
                border-radius: 999px;
                color: #ee0000;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                box-shadow: 0 2px 10px rgba(227, 24, 55, 0.05);
              }

              .connect-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #E31837;
                border-radius: 50%;
                box-shadow: 0 0 8px #E31837;
                animation: pulseGlow 2s infinite;
              }

              .connect-title {
                font-size: 42px;
                font-weight: 800;
                color: #111827;
                letter-spacing: -0.02em;
                margin-bottom: 16px;
                line-height: 1.1;
              }

              .connect-subtitle {
                font-size: 18px;
                color: #6b7280;
                max-width: 600px;
                margin: 0 auto 60px;
                line-height: 1.6;
              }

              /* Contact Cards */
              .connect-card {
                background: #fff;
                border: 1px solid #e5e7eb;
                border-radius: 24px;
                padding: 32px;
                display: flex;
                align-items: center;
                gap: 24px;
                position: relative;
                overflow: hidden;
                transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                height: 100%;
                z-index: 1;
              }

              .connect-card:hover {
                border-color: rgba(227, 24, 55, 0.5);
                transform: translateY(-4px);
                box-shadow: 0 20px 40px -12px rgba(227, 24, 55, 0.15);
              }

              .connect-card-halo {
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                background: radial-gradient(circle at 50% 50%, rgba(227, 24, 55, 0.2), transparent 70%);
                opacity: 0;
                transition: opacity 0.7s ease;
                z-index: -1;
                pointer-events: none;
              }
              .connect-card:hover .connect-card-halo {
                opacity: 1;
              }

              .connect-card-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(227, 24, 55, 0.05) 45%, rgba(227, 24, 55, 0.1) 50%, rgba(227, 24, 55, 0.05) 55%, transparent 60%);
                transition: left 0.5s ease;
                z-index: 0;
                pointer-events: none;
                opacity: 0;
              }
              .connect-card:hover .connect-card-shine {
                left: 100%;
                opacity: 1;
              }

              .connect-icon-box {
                width: 64px;
                height: 64px;
                border-radius: 16px;
                background: linear-gradient(135deg, #ee0000, #ee0000);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                position: relative;
                z-index: 2;
                transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
                flex-shrink: 0;
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.3);
              }

              .connect-card:hover .connect-icon-box {
                transform: scale(1.1);
                box-shadow: 0 8px 20px rgba(227, 24, 55, 0.5);
              }

              .connect-card-content {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                z-index: 2;
              }

              .connect-label {
                font-size: 13px;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                color: #6b7280;
                margin-bottom: 4px;
              }

              .connect-value {
                font-size: 18px;
                font-weight: 700;
                color: #111827;
                transition: color 0.3s ease;
              }
              .connect-card:hover .connect-value {
                color: #ee0000;
              }

              .connect-bottom-glow {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 2px;
                background: linear-gradient(90deg, transparent, #E31837, transparent);
                opacity: 0;
                transition: opacity 0.5s ease;
              }
              .connect-card:hover .connect-bottom-glow {
                opacity: 1;
              }

              /* CTA Card */
              .connect-cta-card {
                background: #fff;
                border: 1px solid rgba(227, 24, 55, 0.3);
                border-radius: 24px;
                padding: 60px 40px;
                position: relative;
                overflow: hidden;
                box-shadow: 0 20px 60px -10px rgba(227, 24, 55, 0.1);
                margin-top: 40px;
                z-index: 1;
              }

              .connect-cta-halo {
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                background: radial-gradient(circle at 50% 0%, rgba(227, 24, 55, 0.15), transparent 70%);
                z-index: -1;
                pointer-events: none;
              }

              .connect-cta-accent {
                position: absolute;
                top: 0;
                left: 20%;
                right: 20%;
                height: 1px;
                background: linear-gradient(90deg, transparent, #E31837, transparent);
                opacity: 0.5;
              }

              .connect-corner-tr {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 40px;
                height: 40px;
                border-top: 2px solid rgba(227, 24, 55, 0.2);
                border-right: 2px solid rgba(227, 24, 55, 0.2);
                border-top-right-radius: 16px;
              }

              .connect-corner-bl {
                position: absolute;
                bottom: 24px;
                left: 24px;
                width: 40px;
                height: 40px;
                border-bottom: 2px solid rgba(227, 24, 55, 0.2);
                border-left: 2px solid rgba(227, 24, 55, 0.2);
                border-bottom-left-radius: 16px;
              }

              .connect-cta-title {
                font-size: 36px;
                font-weight: 800;
                color: #111827;
                margin-bottom: 16px;
              }

              .connect-cta-desc {
                font-size: 18px;
                color: #4b5563;
                max-width: 600px;
                margin: 0 auto 40px;
                line-height: 1.6;
              }

              .connect-btn-primary {
                background: linear-gradient(135deg, #ee0000, #ee0000);
                color: white;
                padding: 14px 32px;
                border-radius: 12px;
                font-weight: 700;
                border: none;
                position: relative;
                overflow: hidden;
                transition: all 0.3s ease;
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.3);
                text-decoration: none;
                display: inline-block;
              }
              .connect-btn-primary:hover {
                transform: scale(1.05);
                box-shadow: 0 8px 20px rgba(227, 24, 55, 0.5);
                color: white;
              }
              .connect-btn-primary:active {
                transform: scale(0.95);
              }

              .connect-btn-secondary {
                background: #fff;
                color: #111827;
                padding: 14px 32px;
                border-radius: 12px;
                font-weight: 700;
                border: 1px solid #e5e7eb;
                position: relative;
                overflow: hidden;
                transition: all 0.3s ease;
                text-decoration: none;
                display: inline-block;
                margin-left: 16px;
              }
              .connect-btn-secondary:hover {
                border-color: #E31837;
                color: #E31837;
                transform: scale(1.05);
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.1);
              }
              .connect-btn-secondary:active {
                transform: scale(0.95);
              }

              @media (max-width: 768px) {
                .connect-title { font-size: 42px; }
                .connect-card { flex-direction: column; text-align: center; padding: 24px; }
                .connect-card-content { align-items: center; }
                .connect-cta-card { padding: 40px 24px; }
                .connect-btn-secondary { margin-left: 0; margin-top: 16px; display: block; width: 100%; }
                .connect-btn-primary { display: block; width: 100%; }
              }

              /* Trusted Section */
              .trusted-section {
                padding: 70px 0 80px 0;
                padding: 0px 0 0px 0;
                background: #fff;
                position: relative;
                overflow: hidden;
              }

              .trusted-orb {
                position: absolute;
                width: 600px;
                height: 600px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.03) 0%, transparent 70%);
                border-radius: 50%;
                pointer-events: none;
                z-index: 0;
              }
              .trusted-orb-1 { top: -10%; left: -10%; }
              .trusted-orb-2 { bottom: -10%; right: -10%; }

              .trusted-badge {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background: #fff;
                border: 1px solid #ffd4d4;
                border-radius: 999px;
                color: #ee0000;
                font-size: 12px;
                font-weight: 700;
                letter-spacing: 0.05em;
                margin-bottom: 24px;
                box-shadow: 0 4px 12px rgba(227, 24, 55, 0.08);
              }
              .trusted-badge-dot {
                width: 6px;
                height: 6px;
                background-color: #E31837;
                border-radius: 50%;
                box-shadow: 0 0 8px #E31837;
                animation: pulseGlow 2s infinite;
              }

              .trusted-title {
                font-size: 42px;
                font-weight: 800;
                color: #111827;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
              }

              .trusted-subtitle {
                font-size: 18px;
                color: #6b7280;
                margin-bottom: 60px;
              }

              .brand-container {
                background: rgba(255, 255, 255, 0.9);
                backdrop-filter: blur(10px);
                border: 1px solid #e5e7eb;
                border-radius: 24px;
                padding: 64px;
                position: relative;
                overflow: hidden;
                box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.05);
                transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                margin-bottom: 80px;
              }

              .brand-container::before {
                content: '';
                position: absolute;
                inset: -2px;
                border-radius: 26px;
                background: #e2f1fe;
                z-index: -1;
                // filter: blur(20px);
              }

              .brand-container:hover {
                border-color: rgba(227, 24, 55, 0.3);
                box-shadow: 0 24px 48px -12px rgba(227, 24, 55, 0.15);
              }

              .brand-accent-line {
                position: absolute;
                top: 0;
                left: 20%;
                right: 20%;
                height: 1px;
                background: linear-gradient(90deg, transparent, #E31837, transparent);
                opacity: 0.5;
              }

              .brand-corner-tr {
                position: absolute;
                top: 24px;
                right: 24px;
                width: 24px;
                height: 24px;
                border-top: 2px solid #e5e7eb;
                border-right: 2px solid #e5e7eb;
                border-top-right-radius: 12px;
                transition: border-color 0.3s ease;
              }
              .brand-corner-bl {
                position: absolute;
                bottom: 24px;
                left: 24px;
                width: 24px;
                height: 24px;
                border-bottom: 2px solid #e5e7eb;
                border-left: 2px solid #e5e7eb;
                border-bottom-left-radius: 12px;
                transition: border-color 0.3s ease;
              }

              .brand-container:hover .brand-corner-tr,
              .brand-container:hover .brand-corner-bl {
                border-color: #E31837;
              }

              .brand-shine {
                position: absolute;
                top: 0;
                left: -100%;
                width: 200%;
                height: 100%;
                background: linear-gradient(115deg, transparent 40%, rgba(227, 24, 55, 0.03) 45%, rgba(227, 24, 55, 0.06) 50%, rgba(227, 24, 55, 0.03) 55%, transparent 60%);
                transition: left 0.7s ease;
                pointer-events: none;
              }
              .brand-container:hover .brand-shine {
                left: 100%;
              }

              .brand-logos-row {
                display: flex;
                justify-content: space-around;
                align-items: center;
                flex-wrap: wrap;
                gap: 40px;
              }
              .brand-logo-item {
                max-height: 48px;
                opacity: 0.7;
                transition: all 0.3s ease;
                // filter: grayscale(100%);
                max-width: 150px;
                object-fit: contain;
              }
              .brand-logo-item:hover {
                opacity: 1;
                filter: grayscale(0%);
                transform: scale(1.05);
              }

              .trusted-stat-item {
                text-align: center;
                padding: 24px;
                transition: transform 0.3s ease;
              }
              .trusted-stat-item:hover {
                transform: translateY(-5px);
              }
              .trusted-stat-value {
                font-size: 48px;
                font-weight: 800;
                color: #111827;
                margin-bottom: 8px;
                position: relative;
                display: inline-block;
                transition: color 0.3s ease, transform 0.3s ease;
              }
              .trusted-stat-item:hover .trusted-stat-value {
                color: #E31837;
                transform: scale(1.1);
              }
              .trusted-stat-value::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 120%;
                height: 120%;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.15) 0%, transparent 70%);
                opacity: 0;
                transition: opacity 0.3s ease;
                z-index: -1;
              }
              .trusted-stat-item:hover .trusted-stat-value::after {
                opacity: 1;
              }
              .trusted-stat-label {
                font-size: 16px;
                font-weight: 600;
                color: #6b7280;
              }

              .faq-card {
                background: #ffffff;
                color: #0f172a;
                border: 1px solid rgba(6, 123, 177, 0.12);
                box-shadow: 0 14px 40px rgba(15, 23, 42, 0.08);
                padding: 28px;
                min-height: 260px;
              }

              .faq-question {
                font-size: 1.05rem;
                font-weight: 700;
                color: #0f172a;
                line-height: 1.35;
              }

              .faq-answer {
                margin-bottom: 0;
                color: #334155;
                font-size: 0.95rem;
                line-height: 1.75;
              }

              .faq-card:not(:last-child) {
                margin-bottom: 0;
              }

              .faq-accordion.modern-faq-accordion .accordion-item {
                border: 1px solid rgba(6, 123, 177, 0.18);
                border-radius: 20px;
                background: #fff;
                box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
                overflow: hidden;
                margin-bottom: 18px;
              }

              .faq-accordion.modern-faq-accordion .accordion-button {
                background: #f7fbff;
                color: #0f172a;
                font-size: 1.05rem;
                font-weight: 700;
                padding: 22px 28px;
                border: none;
                border-radius: 0;
                transition: background 0.3s ease, color 0.3s ease;
              }

              .faq-accordion.modern-faq-accordion .accordion-button:not(.collapsed) {
                background: #e7f2ff;
                color: #04467a;
              }

              .faq-accordion.modern-faq-accordion .accordion-button::after {
                width: 1.2rem;
                height: 1.2rem;
              }

              .faq-accordion.modern-faq-accordion .accordion-body {
                padding: 20px 28px 26px;
                color: #334155;
                font-size: 0.96rem;
                line-height: 1.8;
                background: #fff;
              }

              .faq-accordion.modern-faq-accordion .accordion-item.active .accordion-button {
                box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.05);
              }

              .testimonial-card blockquote {
                font-size: 1rem;
                color: #0f172a;
                line-height: 1.8;
                margin: 0 0 12px 0;
                quotes: "“”‘’";
              }

              .testimonial-card .text-muted {
                color: #64748b !important;
                font-weight: 600;
              }

              .testimonial-carousel .carousel-indicators [data-bs-target] {
                background-color: rgba(6,123,177,0.85);
                width: 10px;
                height: 10px;
                border-radius: 50%;
              }

              .testimonial-carousel .carousel-control-prev-icon,
              .testimonial-carousel .carousel-control-next-icon {
                filter: invert(20%) sepia(80%) saturate(400%) hue-rotate(170deg) brightness(90%);
                background-size: 100%, 100%;
              }

              .testimonial-card.mx-auto {
                padding: 36px;
                border-radius: 16px;
                background: linear-gradient(180deg, #ffffff 0%, #f7fcff 100%);
                border: 1px solid rgba(6,123,177,0.06);
              }

              .testimonial-carousel {
                position: relative;
                padding-bottom: 36px;
                overflow: visible;
              }

              .testimonial-carousel .carousel-indicators {
                position: absolute;
                bottom: -22px;
                left: 50%;
                transform: translateX(-50%);
                justify-content: center;
                gap: 10px;
                margin: 0;
                padding: 0;
                width: auto;
              }

              .testimonial-carousel .carousel-control-prev,
              .testimonial-carousel .carousel-control-next {
                width: 56px;
                height: 56px;
                top: 50%;
                transform: translateY(-50%);
                opacity: 1;
                background: transparent;
                border-radius: 999px;
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .testimonial-carousel .carousel-control-prev {
                left: -72px;
              }

              .testimonial-carousel .carousel-control-next {
                right: -72px;
              }

              .testimonial-card:hover{
              box-shadow:none;
              }

              @media (max-width: 768px) {
                .trusted-title { font-size: 36px; }
                .brand-container { padding: 32px; }
                .brand-logos-row { gap: 24px; }
                .brand-logo-item { max-height: 32px; max-width: 100px; }
              }
              @media (max-width: 992px) {
                .hero-media { margin-top: 40px !important; }
              }

              /* Launch Section Styles */
              .launch-sprint-section {
                padding: 70px 0 80px 0;
                background-color: #fff;
                position: relative;
                overflow: hidden;
              }

              .launch-bg-orb-small {
                position: absolute;
                bottom: 10%;
                right: -5%;
                width: 384px;
                height: 384px;
                background: radial-gradient(circle, rgba(227, 24, 55, 0.08) 0%, transparent 70%);
                border-radius: 50%;
                filter: blur(50px);
                animation: pulseGlow 6s infinite ease-in-out reverse;
                z-index: 0;
              }

              .launch-container {
                position: relative;
                background: linear-gradient(180deg, #effdff 0%, #effdff 100%);
                border-radius: 24px;
                padding: 60px 40px;
                border: 1px solid rgba(56, 164, 229, 0.5);
                box-shadow: 0 0 0 1px rgba(24, 170, 227, 0.2), 0 20px 50px -12px rgba(24, 132, 227, 0.25);
                overflow: hidden;
              }

              .launch-grid-bg {
                position: absolute;
                inset: 0;
                background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
                                  linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
                background-size: 24px 24px;
                opacity: 0.3;
                z-index: 0;
                pointer-events: none;
              }

              .launch-top-accent {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 1px;
                background: linear-gradient(90deg, transparent, rgb(221, 244, 255), transparent);
                box-shadow: 0 1px 10px rgb(239, 253, 255);
                z-index: 2;
              }

              .launch-corner-tr {
                position: absolute;
                top: 20px;
                right: 20px;
                width: 40px;
                height: 40px;
                border-top: 2px solid rgb(221, 244, 255);
                border-right: 2px solid rgb(221, 244, 255);
                border-top-right-radius: 8px;
                z-index: 2;
              }

              .launch-corner-bl {
                position: absolute;
                bottom: 20px;
                left: 20px;
                width: 40px;
                height: 40px;
                border-bottom: 2px solid rgb(221, 244, 255);
                border-left: 2px solid rgb(221, 244, 255);
                border-bottom-left-radius: 8px;
                z-index: 2;
              }

              .launch-content {
                position: relative;
                z-index: 1;
              }

              .launch-title {
                font-size: 42px;
                font-weight: 800;
                color: #000;
                margin-bottom: 16px;
                letter-spacing: -0.02em;
              }

              .launch-subtitle {
                font-size: 18px;
                max-width: 600px;
                margin: 0 auto;
                color: #334155;
              }
              /* Salesforce Delivery Framework Timeline Styling */
              .delivery-timeline {
                width: 100%;
                margin: 60px auto;
                position: relative;
              }
              
              .timeline-grid {
                display: grid;
                grid-template-rows: auto auto auto;
                row-gap: 32px;
                position: relative;
                width: 100%;
              }
              
              .timeline-row {
                display: grid;
                grid-template-columns: repeat(6, 1fr);
                width: 100%;
              }
              
              .timeline-col {
                position: relative;
                padding: 0 12px;
                display: flex;
                flex-direction: column;
                justify-content: center;
              }
              
              .timeline-col.empty {
                height: 0;
                overflow: hidden;
                padding: 0;
              }
              
              .timeline-card {
                background: transparent;
                border: none;
                padding: 10px 0;
                transition: all 0.4s ease;
                box-shadow: none;
                position: relative;
                z-index: 2;
              }
              
              .timeline-card:hover, .timeline-card.active {
                transform: translateY(-5px);
              }

              .timeline-card.card-bottom:hover, .timeline-card.card-bottom.active {
                transform: translateY(5px);
              }
              
              .card-header-row {
                display: flex;
                align-items: center;
                gap: 14px;
                margin-bottom: 14px;
                transition: transform 0.3s ease;
              }
              
              .step-number-badge {
                width: 34px;
                height: 34px;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-weight: 800;
                font-size: 14px;
                flex-shrink: 0;
                box-shadow: 0 4px 12px rgba(6, 123, 177, 0.25);
                border: 1px solid rgba(255, 255, 255, 0.1);
                background: linear-gradient(135deg, #067bb1 0%, #04467a 100%);
              }
              
              .step-title {
                font-size: 18px;
                font-weight: 800;
                color: #0f172a;
                padding-bottom: 0px;
                margin: 0;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                transition: color 0.3s ease;
              }
              
              .timeline-card:hover .step-title,
              .timeline-card.active .step-title {
                color: #067bb1;
              }
              
              .step-text {
                font-size: 13px;
                color: #64748b;
                line-height: 1.5;
                margin: 0;
                padding-left: 2px;
              }
              
              /* Middle Row: Modern Stylish Arrow Track */
              .arrows-row {
                position: relative;
                height: 65px;
                align-items: center;
              }
              
              .arrow-item {
                position: relative;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              
              .arrow-shape {
                position: absolute;
                left: -15px;
                right: -15px;
                top: 5px;
                bottom: 5px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                /* Modern Arrow Shape: Points Right */
                clip-path: polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%, 20px 50%);
                transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                cursor: pointer;
              }
              
              .arrow-item:hover .arrow-shape,
              .arrow-item.active .arrow-shape {
                transform: scale(1.05);
                z-index: 15;
                filter: brightness(1.1);
              }

              .arrow-glow {
                position: absolute;
                inset: 0;
                background: inherit;
                filter: blur(15px);
                opacity: 0;
                transition: opacity 0.4s ease;
                z-index: -1;
              }
              .arrow-item:hover .arrow-glow { opacity: 0.4; }
              
              .arrow-icon {
                color: #fff;
                transition: transform 0.4s ease;
              }
              
              .arrow-item:hover .arrow-icon {
                transform: scale(1.1);
              }
              
              /* Increasing z-index for Standard overlap (Left to Right) */
              .arrow-z-1 { z-index: 6; }
              .arrow-z-2 { z-index: 5; }
              .arrow-z-3 { z-index: 4; }
              .arrow-z-4 { z-index: 3; }
              .arrow-z-5 { z-index: 2; }
              .arrow-z-6 { z-index: 1; }
              
              /* Mobile/Tablet Timeline Styles */
              .mobile-timeline-container {
                display: flex;
                flex-direction: column;
                gap: 24px;
                padding: 20px 0;
              }
              
              .mobile-timeline-item {
                display: flex;
                gap: 20px;
                position: relative;
              }
              
              .mobile-timeline-left {
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 40px;
                flex-shrink: 0;
              }
              
              .mobile-step-badge {
                width: 40px;
                height: 40px;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-weight: 800;
                font-size: 16px;
                z-index: 2;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
              }
              
              .mobile-timeline-line {
                width: 2px;
                position: absolute;
                top: 40px;
                bottom: -24px;
                left: 20px;
                z-index: 1;
                opacity: 0.3;
              }
              
              .mobile-card {
                background: transparent;
                border: none;
                padding: 10px 0;
                flex-grow: 1;
                display: flex;
                align-items: center;
                gap: 16px;
                box-shadow: none;
              }
              
              .mobile-card-icon-box {
                width: 36px;
                height: 36px;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                flex-shrink: 0;
              }
              
              .mobile-step-title {
                font-size: 17px;
                font-weight: 800;
                color: #0f172a;
                margin: 0 0 6px 0;
                text-transform: uppercase;
                letter-spacing: 0.04em;
              }
              
              .mobile-step-text {
                font-size: 13px;
                color: #64748b;
                line-height: 1.4;
                margin: 0;
              }

              @keyframes pulseGlow {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
              }

              /* Circular Services Layout */
              .services-circular-container {
                position: relative;
                width: 100%;
                max-width: 1300px;
                min-height: 500px;
                margin: 40px auto;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              
              .services-center-hub {
                width: 200px;
                height: 200px;
                background: #fff;
                border: 10px solid #E2F1FE;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                text-align: center;
                padding: 20px;
                z-index: 20;
                box-shadow: 0 15px 45px rgba(6, 123, 177, 0.15);
                position: relative;
                transition: all 0.5s ease;
              }

              .services-center-text {
                font-family: 'Outfit', sans-serif;
                font-weight: 800;
                font-size: 20px;
                line-height: 1.2;
                color: #04467a;
                text-transform: uppercase;
                letter-spacing: 0.05em;
              }

              .service-satellite {
                position: absolute;
                width: 90px;
                height: 90px;
                z-index: 30;
                cursor: pointer;
                transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
              }

              .service-satellite-icon-box {
                width: 100%;
                height: 100%;
                background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                box-shadow: 0 10px 25px rgba(6, 123, 177, 0.2);
                border: 5px solid #fff;
                transition: all 0.3s ease;
                z-index: 2;
                outline: 1px solid rgba(6, 123, 177, 0.1);
              }

              .service-satellite:hover .service-satellite-icon-box {
                transform: scale(1.1);
                box-shadow: 0 15px 35px rgba(6, 123, 177, 0.4);
                border-color: #E2F1FE;
              }

              .service-satellite-label {
                position: absolute;
                top: 105%;
                left: 50%;
                transform: translateX(-50%);
                width: 160px;
                text-align: center;
                font-size: 11px;
                font-weight: 700;
                color: #04467a;
                margin-top: 8px;
                line-height: 1.2;
                transition: all 0.3s ease;
                pointer-events: none;
                background: rgba(255, 255, 255, 0.8);
                padding: 4px 4px;
                border-radius: 6px;
                backdrop-filter: blur(4px);
                border: 1px solid rgba(6, 123, 177, 0.1);
              }

              .service-satellite:hover .service-satellite-label,
              .service-satellite.active .service-satellite-label {
                color: #e00;
                transform: translateX(-50%) translateY(2px);
                background: #fff;
                box-shadow: 0 4px 12px rgba(238, 0, 0, 0.1);
                opacity: 1;
              }

              .service-connection-line {
                position: absolute;
                top: 50%;
                left: 50%;
                height: 1px;
                background-image: linear-gradient(to right, rgba(6, 123, 177, 0.3) 50%, transparent 50%);
                background-size: 8px 1px;
                background-repeat: repeat-x;
                transform-origin: left center;
                z-index: 10;
                pointer-events: none;
              }

              .service-details-popover {
                position: absolute;
                right: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 300px;
                background: rgba(255, 255, 255, 0.98);
                backdrop-filter: blur(10px);
                border-radius: 24px;
                padding: 25px;
                box-shadow: 0 25px 60px rgba(6, 123, 177, 0.2);
                z-index: 100;
                border: 1px solid rgba(6, 123, 177, 0.15);
              }

              .service-hover-label-box {
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 300px;
                padding: 32px 32px 32px 0px;
                text-align: left;
                z-index: 100;
              }

              .service-satellite.active .service-satellite-icon-box {
                transform: scale(1.15);
                box-shadow: 0 0 30px rgba(238, 0, 0, 0.5);
                border-color: #ffeaea;
                background: #e00;
              }

              .service-satellite.active::after {
                content: '';
                position: absolute;
                inset: -10px;
                border-radius: 50%;
                border: 2px solid rgba(238, 0, 0, 0.3);
                animation: pulseGlow 2s infinite;
              }

              .service-hover-label-text {
                font-family: 'Outfit', sans-serif;
                font-weight: 800;
                font-size: 32px;
                line-height: 1.1;
                color: #04467a;
                letter-spacing: -0.02em;
              }

              .service-details-title {
                font-weight: 800;
                font-size: 18px;
                color: #04467a;
                margin-bottom: 15px;
                display: flex;
                align-items: center;
                gap: 10px;
                border-bottom: 1px solid #E2F1FE;
                padding-bottom: 10px;
              }

              .service-details-list {
                list-style: none;
                padding: 0;
                margin: 0;
              }

              .service-details-item {
                font-size: 14px;
                color: #475569;
                margin-bottom: 10px;
                display: flex;
                align-items: flex-start;
                gap: 10px;
                line-height: 1.4;
              }

              .service-details-item svg {
                margin-top: 3px;
                flex-shrink: 0;
              }

              @media (min-width: 991px) and (max-width: 1199px) {
                .services-circular-container {
                  height: 680px;
                  margin-top: 10px;
                  margin-bottom: 300px;
                }
                .service-hover-label-box {
                  top: -10px !important;
                  left: 50% !important;
                  transform: translateX(-50%) !important;
                  width: 100% !important;
                  text-align: center !important;
                  padding: 0 !important;
                }
                .service-hover-label-text {
                  font-size: 30px !important;
                  text-align: center !important;
                }
                .service-details-popover {
                  top: calc(100% + 10px) !important;
                  left: 50% !important;
                  transform: translateX(-50%) !important;
                  width: 95% !important;
                  max-width: 550px !important;
                  position: absolute !important;
                }
              }

              @media (max-width: 991px) {
                .services-circular-container {
                  display: none;
                }
              }

              @media (min-width: 992px) {
                .services-mobile-grid {
                  display: none;
                }
              }

@import url('https://fonts.googleapis.com/css2?family=Playball&display=swap');

.playball {
  font-family: 'Playball', cursive !important;
}

            `}</style>

      <div className="gcc-outfit">
        <header className="masthead z-0 pt-130 pb-0">
          <div className="container position-relative">
            <Row className="align-items-center justify-content-between">
              <Col lg="12">
                <div className="pe-3 text-center">
                  {/* <div className="hero-tag mb-3">
                    Salesforce Consulting, Implementation & Managed Services
                  </div> */}
                  <h1 className="hero-title text-black mb-3">
                    Unlock the Full Value of{" "}
                    <span className="highlight-red">Sales<span className="playball">f</span>orce</span> with
                    Expert Consulting, Implementation & Managed Services
                  </h1>
                  <p className="text-black ls-minus-045 fw-500 fs-5 mb-4 opacity-75">
                    Transform customer engagement, streamline operations, and
                    maximize ROI with Comsense&apos;s end-to-end Salesforce
                    consulting services.
                  </p>
                  <div className="hero-chip-row justify-content-center mb-4">
                    <div className="stat-card">
                      <div>
                        <div className="stat-value">
                          • Expert Salesforce strategy{" "}
                        </div>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div>
                        <div className="stat-value">
                          • Modern implementation & support{" "}
                        </div>
                      </div>
                    </div>
                    <div className="stat-card">
                      <div>
                        <div className="stat-value">
                          • AI-enabled business outcomes
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </Col>
              <Col lg="8" className="text-center m-auto">
                <div className="hero-media m-auto">
                  <Image
                    src={flutterHero}
                    alt="Salesforce hero"
                    className="w-100"
                  />
                </div>
                                  <div className="d-flex flex-wrap gap-3 mt-4 justify-content-center">
                    <button
                      onClick={openDataReadinessModal}
                      type="button"
                      className="btn-red whitespace-nowrap"
                    >
                      Book a Free Salesforce Assessment
                    </button>
                  </div>
              </Col>
            </Row>
          </div>
          <div className="hero-bottom-shape">
            <svg width="100%" height="120" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
              <path d="M0 120V80C0 80 160 60 320 70C480 80 640 100 800 90C960 80 1120 40 1280 50C1360 55 1400 70 1440 80V120H0Z" fill="rgb(236, 246, 254)"/>
              <path d="M0 120V95C0 95 160 75 320 85C480 95 640 110 800 100C960 90 1120 55 1280 65C1360 70 1400 85 1440 95V120H0Z" fill="rgb(226, 241, 254)"/>
              <path d="M0 120V105C0 105 160 90 320 95C480 100 640 110 800 105C960 100 1120 85 1280 90C1360 92 1400 98 1440 105V120H0Z" fill="rgb(226, 241, 254)"/>
            </svg>
          </div>
        </header>

        <section className="who-we-are-section py-5 pt-2" style={{backgroundColor: "rgb(226, 241, 254)"}}>
          <Container>
            <Row className="align-items-center g-5">
              <Col xl={7} lg={6} md={12}>
                <h2 className="who-we-are-title">
                  Who <span className="text-blue">We</span> Are
                </h2>
                <p className="who-we-are-text mb-4">
                  Comsense Technologies is a Salesforce + AI consulting partner, helping organizations move from digital transformation to Agentic Transformation where data, intelligence, and execution work in real time.
                           </p>
                <p className="who-we-are-text mb-4">
With deep expertise across the Salesforce ecosystem; including Sales Cloud, Service Cloud, Marketing Cloud, Experience Cloud, Data Cloud, and Commerce Cloud; we help organizations build connected, intelligent customer and business experiences. From consulting, implementation, customization, integration, and migration to AI, automation, and managed services, we support the full Salesforce journey end to end. 
                </p>
                               <p className="who-we-are-text">
                                Whether the goal is improving sales productivity, delivering seamless service, personalizing engagement, or unifying customer data, Comsense helps turn Salesforce into a scalable growth engine.
                               </p>
              </Col>
              <Col xl={5} lg={6} md={12}>
                <div className="who-we-are-image-wrapper">
                  <Image src={whoImage} alt="Who We Are" className="who-we-are-image" />
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="stats-section bg-white">
          <Container>
            <Row className="justify-content-center g-4">
              <Col xl={3} lg={4} md={6} className="d-flex justify-content-center">
                <div className="stat-card-box" ref={ref1}>
                  <div className="stat-decoration-top-left">
                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                      <circle cx="30" cy="30" r="45" fill="#e2f1fe"/>
                      <circle cx="35" cy="35" r="35" fill="#e2f1fe"/>
                      <circle cx="38" cy="38" r="22" fill="#e2f1fe"/>
                    </svg>
                  </div>
                  <div className="stat-decoration-bottom-right">
                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                      <circle cx="110" cy="110" r="50" fill="#e2f1fe"/>
                      <circle cx="105" cy="105" r="40" fill="#e2f1fe"/>
                      <circle cx="102" cy="102" r="28" fill="#e2f1fe"/>
                    </svg>
                  </div>
                  <div className="stat-number-wrapper">
                    <span className="stat-number">{count1}+</span>
                  </div>
                  <p className="stat-label">Technical team</p>
                </div>
              </Col>
              <Col xl={3} lg={4} md={6} className="d-flex justify-content-center">
                <div className="stat-card-box" ref={ref2}>
                  <div className="stat-decoration-top-left">
                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                      <circle cx="30" cy="30" r="45" fill="#e2f1fe"/>
                      <circle cx="35" cy="35" r="35" fill="#e2f1fe"/>
                      <circle cx="38" cy="38" r="22" fill="#e2f1fe"/>                    </svg>
                  </div>
                  <div className="stat-decoration-bottom-right">
                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                      <circle cx="110" cy="110" r="50" fill="#e2f1fe"/>
                      <circle cx="105" cy="105" r="40" fill="#e2f1fe"/>
                      <circle cx="102" cy="102" r="28" fill="#e2f1fe"/>
                    </svg>
                  </div>
                  <div className="stat-number-wrapper">
                    <span className="stat-number">{count2}+</span>
                  </div>
                  <p className="stat-label">Projects Delivered</p>
                </div>
              </Col>
              <Col xl={3} lg={4} md={6} className="d-flex justify-content-center">
                <div className="stat-card-box" ref={ref3}>
                  <div className="stat-decoration-top-left">
                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                      <circle cx="30" cy="30" r="45" fill="#e2f1fe"/>
                      <circle cx="35" cy="35" r="35" fill="#e2f1fe"/>
                      <circle cx="38" cy="38" r="22" fill="#e2f1fe"/>                    </svg>
                  </div>
                  <div className="stat-decoration-bottom-right">
                    <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                      <circle cx="110" cy="110" r="50" fill="#e2f1fe"/>
                      <circle cx="105" cy="105" r="40" fill="#e2f1fe"/>
                      <circle cx="102" cy="102" r="28" fill="#e2f1fe"/>
                    </svg>
                  </div>
                  <div className="stat-number-wrapper">
                    <span className="stat-number">{count3}+</span>
                  </div>
                  <p className="stat-label">Happy Clients</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>



        <section
          className="problem-section"
          style={{ backgroundColor: "#effdff" }}
        >
          <Container>
            <div className="text-center mb-5">
              <h2 className="problem-title">Why Salesforce?</h2>
              <p className="problem-desc">
                Salesforce is the world&apos;s leading CRM platform, helping
                organizations unify sales, service, marketing, commerce, and
                customer data on a single platform. By leveraging Salesforce,
                businesses can automate workflows, improve customer engagement,
                increase productivity, and gain real-time visibility into
                business performance.
              </p>
            </div>

            <Row className="g-4 justify-content-center">
              <Col xl={4} lg={5} md={6}>
                <div className="problem-card">
                  <div className="shape-tr-circle"></div>
                  <div className="shape-tr-inner"></div>
                  <div className="shape-bl-circle"></div>
                  <div className="shape-diamond"></div>
                  <div className="line-top"></div>
                  <div className="line-bottom"></div>
                  <div className="circle-border-anim"></div>
                  <div className="problem-icon-box">
                    <Zap size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="problem-card-title">
                    Business-First Approach
                  </h3>
                  <p className="problem-card-text">
                    We align Salesforce strategy with your business goals,
                    operational priorities, and growth objectives to deliver
                    measurable outcomes.
                  </p>
                </div>
              </Col>
              <Col xl={4} lg={5} md={6}>
                <div className="problem-card">
                  <div className="shape-tr-circle"></div>
                  <div className="shape-tr-inner"></div>
                  <div className="shape-bl-circle"></div>
                  <div className="shape-diamond"></div>
                  <div className="line-top"></div>
                  <div className="line-bottom"></div>
                  <div className="circle-border-anim"></div>
                  <div className="problem-icon-box">
                    <Shield size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="problem-card-title">
                    End-to-End Salesforce Expertise
                  </h3>
                  <p className="problem-card-text">
                    From consulting and implementation to optimization and
                    managed support, we provide complete Salesforce lifecycle
                    services.
                  </p>
                </div>
              </Col>
              <Col xl={4} lg={5} md={6}>
                <div className="problem-card">
                  <div className="shape-tr-circle"></div>
                  <div className="shape-tr-inner"></div>
                  <div className="shape-bl-circle"></div>
                  <div className="shape-diamond"></div>
                  <div className="line-top"></div>
                  <div className="line-bottom"></div>
                  <div className="circle-border-anim"></div>
                  <div className="problem-icon-box">
                    <TrendingUp size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="problem-card-title">
                    AI & Data-Driven Transformation
                  </h3>
                  <p className="problem-card-text">
                    Leverage advanced analytics, automation, AI, and business
                    intelligence to create exceptional customer experiences and
                    improve decision-making.
                  </p>
                </div>
              </Col>
              <Col xl={4} lg={5} md={6}>
                <div className="problem-card">
                  <div className="shape-tr-circle"></div>
                  <div className="shape-tr-inner"></div>
                  <div className="shape-bl-circle"></div>
                  <div className="shape-diamond"></div>
                  <div className="line-top"></div>
                  <div className="line-bottom"></div>
                  <div className="circle-border-anim"></div>
                  <div className="problem-icon-box">
                    <Building2 size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="problem-card-title">
                    Scalable Enterprise Solutions
                  </h3>
                  <p className="problem-card-text">
                    Build future-ready Salesforce environments designed to grow
                    with your business.
                  </p>
                </div>
              </Col>
              <Col xl={4} lg={5} md={6}>
                <div className="problem-card">
                  <div className="shape-tr-circle"></div>
                  <div className="shape-tr-inner"></div>
                  <div className="shape-bl-circle"></div>
                  <div className="shape-diamond"></div>
                  <div className="line-top"></div>
                  <div className="line-bottom"></div>
                  <div className="circle-border-anim"></div>
                  <div className="problem-icon-box">
                    <Users size={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="problem-card-title">
                    Industry-Specific Expertise
                  </h3>
                  <p className="problem-card-text">
                    Deep domain expertise across BFSI, Automotive, Real Estate,
                    Retail & E-Commerce, and Professional Services enables us to
                    deliver solutions tailored to industry-specific requirements
                    and customer expectations.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="page-section services-section bg-white" id="salesforceServices">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg={8} className="text-center">
                <h2 className="outcome-title">Our Salesforce Services</h2>
                <p className="prime-partner-subtitle mb-3">
                  Whether you&apos;re beginning your Salesforce journey or
                  optimizing an existing environment, our experts help you turn
                  Salesforce into a competitive advantage.
                </p>
              </Col>
            </Row>

            <div className="services-circular-container">
              <div className="services-center-hub">
                <div className="services-center-hub-inner">
                  <div className="services-center-text">
                    Comsense
                    <br />
                    Salesforce
                    <br />
                    Services
                  </div>
                </div>
              </div>

              {/* Left side: Hover Label */}
              <div className="service-hover-label-box">
                {hoveredService && (
                  <motion.div
                    initial={{ opacity: 0, x: -20, y: "-50%" }}
                    animate={{ opacity: 1, x: 0, y: "-50%" }}
                    className="service-hover-label-text"
                    style={{ position: "absolute", top: "50%", width: "100%" }}
                  >
                    {hoveredService.title}
                  </motion.div>
                )}
              </div>

              {/* Circular satellites and connections */}
              {salesforceServices.map((service) => {
                const angleRad = (service.angle * Math.PI) / 180;
                const radius = 220; // Increased radius to give space for labels
                const x = Math.cos(angleRad) * radius;
                const y = Math.sin(angleRad) * radius;

                return (
                  <React.Fragment key={service.id}>
                    {/* Connection line */}
                    <div
                      className="service-connection-line"
                      style={{
                        width: `${radius}px`,
                        transform: `rotate(${service.angle}deg)`,
                      }}
                    ></div>

                    {/* Satellite icon */}
                    <motion.div
                      className={`service-satellite ${hoveredService?.id === service.id ? "active" : ""}`}
                      style={{
                        left: `calc(50% + ${x}px - 45px)`,
                        top: `calc(50% + ${y}px - 45px)`,
                      }}
                      onMouseEnter={() => setHoveredService(service)}
                      whileHover={{ scale: 1.1 }}
                    >
                      <div className="service-satellite-icon-box">
                        <service.icon size={36} />
                      </div>
                      <div className="service-satellite-label">
                        {service.title}
                      </div>
                    </motion.div>
                  </React.Fragment>
                );
              })}

              {/* Right side: Popover Details */}
              {hoveredService && (
                <motion.div
                  initial={{ opacity: 0, x: 20, y: "-50%" }}
                  animate={{ opacity: 1, x: 0, y: "-50%" }}
                  className="service-details-popover"
                >
                  <div className="service-details-title">
                    <hoveredService.icon size={22} />
                    {hoveredService.title}
                  </div>
                  <ul className="service-details-list">
                    {hoveredService.details.map((detail, idx) => (
                      <li key={idx} className="service-details-item">
                        <CheckCircle2 size={16} style={{ color: "#067bb1" }} />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>

            {/* Mobile View: Grid of cards */}
            <Row className="services-mobile-grid g-4">
              {salesforceServices.map((service) => (
                <Col md={6} key={service.id}>
                  <div className="outcome-card h-100">
                    <div className="outcome-icon-box mb-4">
                      <service.icon size={28} />
                    </div>
                    <h3 className="fs-5 fw-bold mb-3">{service.title}</h3>
                    <ul className="list-unstyled mb-0">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="small mb-2 opacity-75">
                          <CheckCircle2
                            size={14}
                            className="me-2"
                            style={{ color: "#067bb1" }}
                          />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        <section className="why-section">
          <div className="why-orb-1"></div>
          <div className="why-orb-2"></div>
          <Container className="position-relative z-1">
            <Row className="justify-content-center mb-5 text-center">
              <Col lg={8}>
                <h2 className="why-title">Salesforce Cloud Expertise</h2>
                <p className="why-subtitle">
                  We support the full Salesforce ecosystem with deep expertise
                  across the most strategic clouds and platforms.
                </p>
              </Col>
            </Row>
            <Row className="g-4">
              {[
                {
                  icon: Cloud,
                  title: "Sales Cloud",
                  text: "Increase sales productivity, improve forecasting accuracy, and accelerate revenue growth.",
                },
                {
                  icon: Users,
                  title: "Service Cloud",
                  text: "Deliver seamless customer support experiences across channels.",
                },
                {
                  icon: TrendingUp,
                  title: "Marketing Cloud",
                  text: "Create personalized customer journeys and targeted marketing campaigns.",
                },
                {
                  icon: Building2,
                  title: "Experience Cloud",
                  text: "Build connected digital experiences for customers, partners, and employees.",
                },
                {
                  icon: Shield,
                  title: "Data Cloud",
                  text: "Unify customer data across systems for a complete 360-degree customer view.",
                },
                {
                  icon: Settings,
                  title: "Commerce Cloud",
                  text: "Deliver seamless omnichannel commerce experiences.",
                },
              ].map((cloud) => (
                <Col lg={4} md={6} key={cloud.title}>
                  <div className="why-card h-100">
                    <div className="why-card-halo"></div>
                    <div className="why-card-shine"></div>
                    <div className="why-accent-line"></div>
                    <div className="why-corner-l"></div>
                    <div className="why-corner-orb"></div>
                    <div className="why-bottom-glow"></div>
                    <div className="why-icon-box">
                      <cloud.icon size={28} strokeWidth={1.5} />
                      <div className="why-icon-glow"></div>
                    </div>
                    <h3 className="why-card-title">{cloud.title}</h3>
                    <p className="why-card-desc">{cloud.text}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        <section className="problem-section">
          <Container>
            <div className="text-center mb-5">
              <h2 className="problem-title">
                Industry-Specific Salesforce Expertise
              </h2>
              <p className="problem-desc">
                Deep expertise across BFSI, Automotive, Real Estate, Retail &
                E-Commerce, and Professional Services enables us to deliver
                Salesforce solutions tailored to industry-specific workflows,
                compliance requirements, and customer expectations.
              </p>
            </div>
            <Row className="g-4 justify-content-center">
              {[
                {
                  icon: Car,
                  title: "Automotive",
                  text: "Accelerate lead management, dealer engagement, test-drive scheduling, customer retention, after-sales service, and vehicle lifecycle management.",
                },
                {
                  icon: Home,
                  title: "Real Estate",
                  text: "Streamline lead nurturing, property sales management, broker collaboration, site visit tracking, and sales forecasting.",
                },
                {
                  icon: ShoppingBag,
                  title: "Retail & E-Commerce",
                  text: "Deliver personalized shopping experiences, loyalty programs, omnichannel engagement, and AI-driven recommendations.",
                },
                {
                  icon: Shield,
                  title: "BFSI",
                  text: "Transform customer acquisition, onboarding, loan origination, policy servicing, relationship management, compliance monitoring, and customer engagement.",
                },
                {
                  icon: Briefcase,
                  title: "Professional Services",
                  text: "Improve client acquisition, project visibility, resource utilization, engagement management, and service delivery through a unified platform.",
                },
              ].map((industry) => (
                <Col xl={4} lg={5} md={6} key={industry.title}>
                  <div
                    className="problem-card"
                    style={{
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                      border: "2px solid #e5e7eb",
                    }}
                  >
                    <div className="shape-tr-circle"></div>
                    <div className="shape-tr-inner"></div>
                    <div className="shape-bl-circle"></div>
                    <div className="shape-diamond"></div>
                    <div className="line-top"></div>
                    <div className="line-bottom"></div>
                    <div className="circle-border-anim"></div>
                    <div className="problem-icon-box">
                      <industry.icon size={28} strokeWidth={1.5} />
                    </div>
                    <h3 className="problem-card-title">{industry.title}</h3>
                    <p className="problem-card-text">{industry.text}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        <section className="why-section">
          <div className="why-orb-1"></div>
          <div className="why-orb-2"></div>
          <Container className="position-relative z-1">
            <Row className="justify-content-center mb-5 text-center">
              <Col lg={8}>
                <h2 className="why-title">Our Salesforce Delivery Framework</h2>
                <p className="why-subtitle">
                  A structured delivery approach that helps you move from
                  strategy to adoption with clarity, speed, and measurable
                  outcomes.
                </p>
              </Col>
            </Row>
            <div className="delivery-timeline mb-0">
              {/* Desktop Horizontal Timeline - Standard (Left to Right) */}
              <div className="timeline-desktop d-none d-lg-block">
                <div className="timeline-grid">
                  {/* Top Row: Steps 1, 3, 5 */}
                  <div className="timeline-row top-row">
                    {/* Step 1: Discover */}
                    <div className="timeline-col">
                      <div
                        className={`timeline-card card-top ${activeStep === 0 ? "active" : ""}`}
                        onMouseEnter={() => setActiveStep(0)}
                        onMouseLeave={() => setActiveStep(null)}
                      >
                        <div className="card-header-row">
                          <span
                            className="step-number-badge"
                            style={{ backgroundColor: "#067bb1" }}
                          >
                            1
                          </span>
                          <h4 className="step-title">Discover</h4>
                        </div>
                        <p className="step-text">
                          Assess business objectives, challenges, and
                          opportunities.
                        </p>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="timeline-col empty"></div>

                    {/* Step 3: Build */}
                    <div className="timeline-col">
                      <div
                        className={`timeline-card card-top ${activeStep === 2 ? "active" : ""}`}
                        onMouseEnter={() => setActiveStep(2)}
                        onMouseLeave={() => setActiveStep(null)}
                      >
                        <div className="card-header-row">
                          <span
                            className="step-number-badge"
                            style={{ backgroundColor: "#155fa6" }}
                          >
                            3
                          </span>
                          <h4 className="step-title">Build</h4>
                        </div>
                        <p className="step-text">
                          Configure, customize, and integrate Salesforce
                          solutions.
                        </p>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="timeline-col empty"></div>

                    {/* Step 5: Deploy */}
                    <div className="timeline-col">
                      <div
                        className={`timeline-card card-top ${activeStep === 4 ? "active" : ""}`}
                        onMouseEnter={() => setActiveStep(4)}
                        onMouseLeave={() => setActiveStep(null)}
                      >
                        <div className="card-header-row">
                          <span
                            className="step-number-badge"
                            style={{ backgroundColor: "#2381cc" }}
                          >
                            5
                          </span>
                          <h4 className="step-title">Deploy</h4>
                        </div>
                        <p className="step-text">
                          Execute rollout, migration, and user training.
                        </p>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="timeline-col empty"></div>
                  </div>

                  {/* Middle Row: Arrow Track (Standard Left to Right) */}
                  <div className="timeline-row arrows-row">
                    {[
                      { icon: Brain, color: "#067bb1", class: "arrow-z-1" },
                      { icon: Building2, color: "#0a568c", class: "arrow-z-2" },
                      { icon: Settings, color: "#155fa6", class: "arrow-z-3" },
                      {
                        icon: ShieldCheck,
                        color: "#1c6fbc",
                        class: "arrow-z-4",
                      },
                      { icon: Repeat, color: "#2381cc", class: "arrow-z-5" },
                      { icon: Zap, color: "#48a6e0", class: "arrow-z-6" },
                    ].map((arrow, idx) => {
                      const Icon = arrow.icon;
                      return (
                        <div
                          key={idx}
                          className={`arrow-item ${arrow.class} ${activeStep === idx ? "active" : ""}`}
                          onMouseEnter={() => setActiveStep(idx)}
                          onMouseLeave={() => setActiveStep(null)}
                        >
                          <div
                            className="arrow-shape"
                            style={{ backgroundColor: arrow.color }}
                          >
                            <div className="arrow-glow"></div>
                            <Icon
                              className="arrow-icon"
                              size={26}
                              strokeWidth={1.75}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom Row: Steps 2, 4, 6 */}
                  <div className="timeline-row bottom-row">
                    {/* Spacer */}
                    <div className="timeline-col empty"></div>

                    {/* Step 2: Design */}
                    <div className="timeline-col">
                      <div
                        className={`timeline-card card-bottom ${activeStep === 1 ? "active" : ""}`}
                        onMouseEnter={() => setActiveStep(1)}
                        onMouseLeave={() => setActiveStep(null)}
                      >
                        <div className="card-header-row">
                          <span
                            className="step-number-badge"
                            style={{ backgroundColor: "#0a568c" }}
                          >
                            2
                          </span>
                          <h4 className="step-title">Design</h4>
                        </div>
                        <p className="step-text">
                          Develop solution architecture and implementation
                          roadmap.
                        </p>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="timeline-col empty"></div>

                    {/* Step 4: Validate */}
                    <div className="timeline-col">
                      <div
                        className={`timeline-card card-bottom ${activeStep === 3 ? "active" : ""}`}
                        onMouseEnter={() => setActiveStep(3)}
                        onMouseLeave={() => setActiveStep(null)}
                      >
                        <div className="card-header-row">
                          <span
                            className="step-number-badge"
                            style={{ backgroundColor: "#1c6fbc" }}
                          >
                            4
                          </span>
                          <h4 className="step-title">Validate</h4>
                        </div>
                        <p className="step-text">
                          Conduct testing and user acceptance validation.
                        </p>
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="timeline-col empty"></div>

                    {/* Step 6: Optimize */}
                    <div className="timeline-col">
                      <div
                        className={`timeline-card card-bottom ${activeStep === 5 ? "active" : ""}`}
                        onMouseEnter={() => setActiveStep(5)}
                        onMouseLeave={() => setActiveStep(null)}
                      >
                        <div className="card-header-row">
                          <span
                            className="step-number-badge"
                            style={{ backgroundColor: "#48a6e0" }}
                          >
                            6
                          </span>
                          <h4 className="step-title">Optimize</h4>
                        </div>
                        <p className="step-text">
                          Continuously improve performance, adoption, and
                          business outcomes.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Vertical Timeline - Standard */}
              <div className="timeline-mobile d-block d-lg-none">
                <div className="mobile-timeline-container">
                  {[
                    {
                      icon: Brain,
                      title: "Discover",
                      text: "Assess business objectives, challenges, and opportunities.",
                      color: "#067bb1",
                    },
                    {
                      icon: Building2,
                      title: "Design",
                      text: "Develop solution architecture and implementation roadmap.",
                      color: "#0a568c",
                    },
                    {
                      icon: Settings,
                      title: "Build",
                      text: "Configure, customize, and integrate Salesforce solutions.",
                      color: "#155fa6",
                    },
                    {
                      icon: ShieldCheck,
                      title: "Validate",
                      text: "Conduct testing and user acceptance validation.",
                      color: "#1c6fbc",
                    },
                    {
                      icon: Repeat,
                      title: "Deploy",
                      text: "Execute rollout, migration, and user training.",
                      color: "#2381cc",
                    },
                    {
                      icon: Zap,
                      title: "Optimize",
                      text: "Continuously improve performance, adoption, and business outcomes.",
                      color: "#48a6e0",
                    },
                  ].map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.title} className="mobile-timeline-item">
                        <div className="mobile-timeline-left">
                          <div
                            className="mobile-step-badge"
                            style={{ backgroundColor: item.color }}
                          >
                            {idx + 1}
                          </div>
                          {idx < 5 && (
                            <div
                              className="mobile-timeline-line"
                              style={{ background: item.color }}
                            />
                          )}
                        </div>
                        <div className="mobile-timeline-right">
                          <div className="mobile-card">
                            <div
                              className="mobile-card-icon-box"
                              style={{ backgroundColor: item.color }}
                            >
                              <Icon size={20} />
                            </div>
                            <div className="mobile-card-body">
                              <h4 className="mobile-step-title">
                                {item.title}
                              </h4>
                              <p className="mobile-step-text">{item.text}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="outcomes-butterfly-section">
          <Container>
            <Row className="justify-content-center mb-4">
              <Col lg={8} className="text-center">
                <h2 className="problem-title mb-0">
                  Business Outcomes We Deliver
                </h2>
              </Col>
            </Row>

            <div className="butterfly-container">
              {/* SVG Connection Layer */}
              <svg className="butterfly-svg-layer" viewBox="0 0 1100 700">
                <defs>
                  <linearGradient
                    id="gradLeft"
                    x1="100%"
                    y1="0%"
                    x2="0%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      style={{ stopColor: "#067bb1", stopOpacity: 0.6 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: "#04467a", stopOpacity: 0.1 }}
                    />
                  </linearGradient>
                  <linearGradient
                    id="gradRight"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      style={{ stopColor: "#067bb1", stopOpacity: 0.6 }}
                    />
                    <stop
                      offset="100%"
                      style={{ stopColor: "#04467a", stopOpacity: 0.1 }}
                    />
                  </linearGradient>
                </defs>

                {/* Left Side Paths */}
                <path
                  d="M 550 350 L 380 120"
                  className="connection-path"
                  stroke="url(#gradLeft)"
                />
                <path
                  d="M 550 350 L 380 280"
                  className="connection-path"
                  stroke="url(#gradLeft)"
                />
                <path
                  d="M 550 350 L 380 420"
                  className="connection-path"
                  stroke="url(#gradLeft)"
                />
                <path
                  d="M 550 350 L 380 580"
                  className="connection-path"
                  stroke="url(#gradLeft)"
                />

                {/* Right Side Paths */}
                <path
                  d="M 550 350 L 720 120"
                  className="connection-path"
                  stroke="url(#gradRight)"
                />
                <path
                  d="M 550 350 L 720 280"
                  className="connection-path"
                  stroke="url(#gradRight)"
                />
                <path
                  d="M 550 350 L 720 420"
                  className="connection-path"
                  stroke="url(#gradRight)"
                />
                <path
                  d="M 550 350 L 720 580"
                  className="connection-path"
                  stroke="url(#gradRight)"
                />
              </svg>

              {/* Left Wing */}
              <div className="butterfly-wing butterfly-wing-left">
                {[
                  {
                    num: "01",
                    text: "Faster lead-to-revenue cycles",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: Zap,
                  },
                  {
                    num: "02",
                    text: "Improved customer engagement",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: Users,
                  },
                  {
                    num: "03",
                    text: "Increased sales productivity",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: TrendingUp,
                  },
                  {
                    num: "04",
                    text: "Better customer retention",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: ShieldCheck,
                  },
                ].map((item) => (
                  <div className="outcome-item-butterfly" key={item.num}>
                    <div
                      className="outcome-number-badge"
                      style={{ background: item.color }}
                    >
                      <item.icon size={16} />
                    </div>
                    <div className="outcome-card-butterfly">
                      <div
                        className="outcome-dot"
                        style={{ background: item.color }}
                      />
                      {item.text}
                    </div>
                  </div>
                ))}
              </div>

              {/* Center Circle */}
              <div className="butterfly-center">
                <div className="butterfly-center-inner">
                  <div className="butterfly-center-icon">
                    <TrendingUp size={48} />
                  </div>
                  <div className="butterfly-center-title">
                    Business
                    <br />
                    Outcomes
                  </div>
                </div>
              </div>

              {/* Right Wing */}
              <div className="butterfly-wing butterfly-wing-right">
                {[
                  {
                    num: "05",
                    text: "Enhanced operational efficiency",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: Cloud,
                  },
                  {
                    num: "06",
                    text: "Greater business visibility",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: Eye,
                  },
                  {
                    num: "07",
                    text: "Reduced manual processes through automation",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: Repeat,
                  },
                  {
                    num: "08",
                    text: "Higher return on Salesforce investments",
                    color: "linear-gradient(180deg, #067bb1 0%, #04467a 100%)",
                    icon: DollarSign,
                  },
                ].map((item) => (
                  <div className="outcome-item-butterfly" key={item.num}>
                    <div
                      className="outcome-number-badge"
                      style={{ background: item.color }}
                    >
                      <item.icon size={16} />
                    </div>
                    <div className="outcome-card-butterfly">
                      <div
                        className="outcome-dot"
                        style={{ background: item.color }}
                      />
                      {item.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </section>

        <section
          className="launch-sprint-section testimonials-section"
          id="testimonials"
        >
          <div className="launch-bg-orb-large"></div>
          <div className="launch-bg-orb-small"></div>
          <Container className="position-relative z-1">
            <motion.div
              className="launch-containers"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="launch-content">
                <div className="text-center mb-5">
                  {/* <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="launch-badge"
                  >
                    TESTIMONIALS
                  </motion.div> */}
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="launch-title"
                  >
                    Testimonials
                  </motion.h2>
                </div>

                <Row className="g-4">
                  <Col lg={10} className="mx-auto">
                    <Carousel
                      controls
                      indicators
                      interval={7000}
                      pause={false}
                      fade
                      className="testimonial-carousel"
                    >
                      {[
                        {
                          text: "We truly appreciate the exceptional performance of the Comsense team, from strategizing the campaign to flawless execution. Their efforts helped us recover lost customers and drive in significant additional revenue. We are excited to continue scaling more initiatives together.",
                          author:
                            "— Head of Marketing, Royal Enfield",
                        },
                        {
                          text: "During the project scoping phase, the team collaborated closely with all business owners, critically assessing data needs and evaluating operational feasibility for each use case. Their expertise enabled us to define and prioritize use cases effectively based on requirements, resolve data-related issues, and ensure a seamless project orchestration.",
                          author:
                            "— Tata Motors, Digital Transformation Leader",
                        },
                      ].map((t, i) => (
                        <Carousel.Item key={i}>
                          <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.15 + i * 0.03 }}
                            className="testimonial-card launch-card mx-auto"
                            style={{ maxWidth: 900 }}
                          >
                            <div
                              className="mb-3"
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "12px",
                              }}
                            >
                              <Quote
                                size={32}
                                className="text-opacity-15"
                                style={{
                                  color: "rgba(6,123,177,0.15)",
                                  flexShrink: 0,
                                  marginTop: "-4px",
                                  transform: "rotate(180deg)",
                                }}
                              />
                              <blockquote style={{ margin: 0 }}>
                                {t.text}
                              </blockquote>
                            </div>
                            <div className="text-end text-muted">
                              {t.author}
                            </div>
                          </motion.div>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </Col>
                </Row>
              </div>
            </motion.div>
          </Container>
        </section>

        <section className="faq-section" id="FAQs">
          <Container>
            <Row className="justify-content-center mb-5">
              <Col lg={8} className="text-center">
                <h2 className="launch-title mb-0">FAQs</h2>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col lg={10}>
                <Accordion
                  defaultActiveKey="0"
                  className="faq-accordion modern-faq-accordion"
                >
                  {[
                    {
                      question: "What Salesforce services does Comsense offer?",
                      answer:
                        "Comsense provides Salesforce consulting, implementation, customization, integration, migration, managed services, training, and ongoing support.",
                    },
                    {
                      question: "What does a Salesforce consulting partner do?",
                      answer:
                        "A Salesforce consulting partner helps organizations design, implement, optimize, and manage Salesforce solutions aligned with business objectives.",
                    },
                    {
                      question: "How long does Salesforce implementation take?",
                      answer:
                        "Implementation timelines vary based on complexity and requirements, typically ranging from several weeks to a few months.",
                    },
                    {
                      question:
                        "Can Comsense integrate Salesforce with existing systems?",
                      answer:
                        "Yes. We integrate Salesforce with ERP systems, finance applications, marketing platforms, Microsoft solutions, and third-party business applications.",
                    },
                    {
                      question:
                        "Do you provide Salesforce customization and development services?",
                      answer:
                        "Yes. Our experts develop custom workflows, Lightning components, dashboards, portals, automation solutions, and industry-specific applications.",
                    },
                    {
                      question: "What are Salesforce Managed Services?",
                      answer:
                        "Managed Services provide ongoing administration, support, monitoring, optimization, governance, and enhancements to ensure long-term Salesforce success.",
                    },
                    {
                      question:
                        "Can Comsense migrate data from legacy CRM systems?",
                      answer:
                        "Yes. We provide secure migration services from legacy CRM platforms while maintaining data integrity and minimizing business disruption.",
                    },
                  ].map((faq, index) => (
                    <Accordion.Item
                      eventKey={String(index)}
                      key={faq.question}
                      className="faq-card-item"
                    >
                      <Accordion.Header>{faq.question}</Accordion.Header>
                      <Accordion.Body>{faq.answer}</Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Final CTA Section */}
        <section className="launch-sprint-section pt-0">
          <Container>
            <motion.div
              className="launch-container pt-0 pb-0 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="launch-top-accent"></div>
              <div className="launch-corner-tr"></div>
              <div className="launch-corner-bl"></div>
              <div className="launch-bg-small"></div>
              <div className="launch-grid-bg"></div>
              <div className="launch-content py-5">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="launch-title mb-3"
                >
                  Ready to Accelerate Growth with Salesforce?
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="launch-subtitle mb-4"
                >
                  Discover how Comsense can help you streamline operations,
                  improve customer engagement, and maximize ROI from your
                  Salesforce investment.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <button
                    type="button"
                    className="btn-red text-decoration-none d-inline-block"
                    onClick={openDataReadinessModal}
                  >
                    Book a Free Salesforce Assessment
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </Container>
        </section>
        <Modal
          show={showDataReadinessModal}
          size="xl"
          onHide={closeDataReadinessModal}
          aria-labelledby="contained-modal-title-vcenter"
          centered
          style={{ overflow: "hidden" }}
        >
          <style>
            {`.btn-close {
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                    z-index: 2;
                  }
                  .modal-content{overflow:hidden !important;}
                  `}
          </style>
          <Modal.Header className="p-0" closeButton></Modal.Header>
          <Modal.Body className="p-0">
            <DataReadinessModalContent
              key={showDataReadinessModal ? "open" : "closed"}
            />
          </Modal.Body>
        </Modal>

        <Footer hideQuickLinks={true} hideSocialMedia={true} />
      </div>
    </>
  );
};

export default SalesforceLanding;
